import {
  booleanValues,
  CustomButton,
  DropdownType,
  ExportButtonDropdown,
  filterOperatorValues,
  FORMAT_PATTERN,
  formatPhoneNumber,
  Pagination,
  SORT_KEYS,
  TableActionType,
  TableColumnType,
  Tabs,
  TENANTS,
  Tooltip,
} from '@ui-kit'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  AuthUserClient,
  DataTypeStore,
  filterCorrespondenceLogsData,
  filterDataOnScroll,
  hideLoader,
  showLoader,
  updateCorrespondenceFilterData,
  NewPaginationRequestCorrespondenceLogs,
  NewJsonData,
  NewAttachment,
  getCaseCommunicationIdByApi,
  getGenericEntityListingAPI,
  getAllEntities,
  IEntityType,
  setLocalEntityObject,
  getServiceByServiceName,
  correspondenceInboundWebData,
  setClientDropdown,
  SearchCriteria,
  getEntityDataByEntityNameApiForFilter,
  fetchTypeStatusForCommunications,
  exportEntityDataByEntityIdLayoutIdApiForFilter,
  addToaster,
  ToasterTypeValues,
  ToasterType,
} from '@app-store'
import {
  ACCESSOR_EXPORT_MAP,
  COMM_TEMPLATE,
  COMMUNICATION_ACCESSOR_KEYS,
  COMMUNICATION_SELECT_FIELDS,
  COMMUNICATION_UI_MAPPINGS,
  COMMUNICATION_UI_MAPPINGS_INBOUND_FAX,
  COMMUNICATION_UI_MAPPINGS_INBOUND_SMS,
  communicationKeyMap,
  communicationSortKeyMap,
  correspondenceLogsPage,
  emailTemplate,
  ENTITY_ALIASES,
  ENTITY_IMPORTANT_KEYS,
  EXCLUDE_FIELDS_STANDARD,
  EXPORT_FIELD_NAMES,
  EXPORT_SELECT_FIELDS,
  FIELD_VALUE_CONSTANTS,
  FIXED_TABS,
  HTTP_STATUS_CONSTANTS,
  NAMESPACES,
  PERMISSION_MODULES,
  RELATED_ENTITIES,
  searchValues,
} from '../../utils/constants'
import PreviewCorrespondenceLogs from './PreviewCorrespondenceLogs'
import {
  CorrespondenceOptions,
  FilterBlock,
  FilterEntry,
  FinalPayload,
  InboundWebPayloadType,
  InputItem,
  RelatedEntity,
  SearchTableCriteria,
  TableFilterObjectBody,
  TransformedDataType,
} from './correspondenceTypes'
import { isEmpty, uniqueId } from 'lodash'
import './Correspondence.css'
import {
  CORRESPONDENCE_LOGS_STRINGS,
  TABLE_ACTIONS_NAMESPACE,
} from '../../utils/strings'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import PersistTable from '../../common/PersistTable'
import {
  convertToCamelCase,
  convertToUTCISOString,
  downloadCSV,
  getDefaultActiveTabName,
  parseErrorMessage,
  permissionObject,
  programPermissionsObject,
  resendCommunication,
  trimObjects,
} from '../../utils/helperFunctions'
import { Column, DropDownOption, TableColumn } from '../../types/types'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  CorrespondenceLogsTableType,
  ExportExtensionType,
  ExportPayloadType,
  InboundColumnType,
  UiDataMappingsType,
  RelatedEntityType,
} from '../../types/correspondenceLogs.types'
import { handleOpenLink } from '../../utils/dynamic-queue'
import { formatLocalDateWithZone } from '../../utils/date.utils'
import { isReadOnlyUser } from '../../utils/permission'
import CommunicationStatus from './CommunicationStatus'
import { QUEUE_STRINGS } from '../../utils/queueIntake.types'
import { isAxiosError } from 'axios'

const getFields = (uniqueKey: string) => {
  const fields = [
    correspondenceLogsPage.CASES,
    correspondenceLogsPage.MODE_KEY_UPPERCASE,
    correspondenceLogsPage.STATUS_KEY_UPPERCASE,
    correspondenceLogsPage.PROGRAM_KEY_UPPERCASE,
    correspondenceLogsPage.SERVICE_KEY_UPPERCASE,
    correspondenceLogsPage.DATE_AND_TIME_KEY_UPPERCASE,
    correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE,
  ]

  if (uniqueKey === correspondenceLogsPage.OUTBOUND_LOWERCASE) {
    fields.push(correspondenceLogsPage.TEMPLATE_KEY_UPPERCASE)
  }

  return fields
}

const getSortValue = (
  sortDirection: {
    keyName: string
    direction: SORT_KEYS
    isAsc: SORT_KEYS
    key: string
  },
  uniqueKey?: string
) => {
  if (
    sortDirection?.key &&
    sortDirection.key === 'created_at' &&
    (uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
      uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE)
  ) {
    return { key: 'received_at', isAsc: sortDirection.isAsc }
  } else if (sortDirection.key && !isEmpty(sortDirection.key)) {
    return { key: sortDirection.key, isAsc: sortDirection.isAsc }
  } else {
    return null
  }
}

let filteredPayload: FinalPayload

const CorrespondenceLogsTable = ({
  uniqueKey,
  caseId,
  isCaseView,
}: CorrespondenceLogsTableType) => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { programPermissions, programs } = useAppSelector(
    (state: RootState) => state?.clientPermissions
  )
  const entityObject = useAppSelector(
    (state) => state?.localCommon?.entityObject
  )
  const isSuperAdmin = useAppSelector(
    (state) => state?.authUser?.user?.superAdmin
  )

  const keyMap: Record<string, string> = {
    COMMUNICATION_ID: 'enrollment_id',
    CASE_ID: 'c_case.case_key',
    CASE_TYPE: 'c_case.services.service_name',
    PROGRAM: 'hcp_program.operational_program_name',
    PRODUCT: 'product.trademarked_name',
    RECEIVED_FROM: 'received_from',
    PATIENT_FIRST_NAME: 'patient_first_name',
    PATIENT_LAST_NAME: 'patient_last_name',
    PRESCRIBER_FIRST_NAME: 'prescriber_first_name',
    PRESCRIBER_LAST_NAME: 'prescriber_last_name',
    FACILITY_NAME_NEW: 'facility_name_new',
    REQUESTOR_TYPE: 'requestor_type',
    QUICK_DENIAL_REASON: 'quick_denial_reason',
    RECEIVED_AT: 'created_at',
    AGENT_NAME: 'users.first_name',
    STATUS: 'enrollment_status.label',
  }

  const searchKeyToEntityMap: Record<string, string> = {
    CASE_TYPE: 'services',
    PROGRAM: 'programs',
    PRODUCT: 'products',
  }
  const searchKeyToFieldMap: Record<string, string> = {
    CASE_TYPE: 'service_name',
    PROGRAM: 'operational_program_name',
    PRODUCT: 'trademarked_name',
    STATUS: 'web-enrollment-status',
  }

  const sortKeyMap: Record<string, string> = {
    communication_id: 'enrollment_id',
    case_id: 'c_case.case_key',
    case_type: 'c_case.services.service_name',
    program: 'hcp_program.operational_program_name',
    product: 'product.trademarked_name',
    received_from: 'received_from',
    received_at: 'created_at',
    agent_name: 'users.first_name',
    status: 'enrollment_status.label',
  }

  const [correspondenceLogFormData, setCorrespondenceLogFormData] =
    useState<CorrespondenceOptions>({
      communication_id: [],
      case_id: [],
      template_name: [],
      communication_type: [],
      program: [],
      service: [],
      status: [],
    })

  const isInitialCall = useRef<boolean>(true)
  const [staticURL, setStaticURL] = useState<string>('')
  const [dynamicURL, setDynamicURL] = useState<string>('')
  const [inboundBulk, setInboundBulk] = useState<boolean>(false)
  const [showTableType, setShowTableType] = useState<string>(uniqueKey)
  const [defaultActiveTab, setDefaultActiveTab] = useState<{
    label: string
    component: React.ReactNode
  }>({
    label: getDefaultActiveTabName(uniqueKey),
    component: null,
  })

  const [tableData, setTableData] = useState<TransformedDataType[]>()
  const [inboundWebTableData, setInboundWebTableData] =
    useState<TransformedDataType[]>()
  const [inboundWebPagination, setInboundWebPagination] = useState<{
    numberOfElements: number
    totalPages: number
    totalElements: number
  }>({
    numberOfElements: 0,
    totalPages: 0,
    totalElements: 0,
  })
  const [apiFilter, setApiFilter] = useState<TableFilterObjectBody>({
    fields: null,
    search_criteria: {},
  })
  const [searchFromTableCriterias, setSearchFromTableCriterias] = useState<
    SearchTableCriteria[]
  >([])
  const [emptyDataMessage, setEmptyDataMessage] = useState<string>('')
  const [previewData, setPreviewData] = useState<{
    data?: NewJsonData | undefined
    documentEntities?: NewAttachment[] | undefined
    type?: string
    communicationId?: number
    faxName?: string
    row?: TransformedDataType
    status?: string
    recordId?: number
  }>({
    data: {},
    documentEntities: [],
    type: '',
  })

  const permissionsData = useRef<{
    viewPermissions?: string[]
    hasManagePermissions?: boolean
  }>({})

  const [currentPage, setCurrentPage] = useState<number>(
    FIELD_VALUE_CONSTANTS.ONE
  )
  const [rowPerPage, setRowPerPage] = useState<number>(
    FIELD_VALUE_CONSTANTS.TEN
  )
  const [sizePerScroll, setSizePerScroll] = useState<number>(
    FIELD_VALUE_CONSTANTS.TWENTY
  )
  const [scrollRowPage, setScrollRowPage] = useState<number>(
    FIELD_VALUE_CONSTANTS.ZERO
  )
  const [sortDirection, setSortDirection] = useState<{
    keyName: string
    direction: SORT_KEYS
    isAsc: SORT_KEYS
    key: string
  }>({
    key: correspondenceLogsPage.DATE_AND_TIME_KEY,
    isAsc: SORT_KEYS.Desc,
    keyName: correspondenceLogsPage.DATE_AND_TIME_KEY_CAMELCASE,
    direction: SORT_KEYS.Desc,
  })
  const [allOptions, setAllOptions] = useState<{
    [key: string]: {
      label: string
      value: string
    }[]
  }>({
    communication_id: [],
    case_id: [],
    template_name: [],
    communication_type: [],
    status: [],
    service: [],
    program: [],
  })
  const totalPages = useAppSelector(
    (state: RootState) => state?.correspondenceLogs?.totalPages
  )
  const correspondenceFilteredLogsData = useAppSelector(
    (state) => state?.correspondenceLogs?.filteredContent
  ) as NewPaginationRequestCorrespondenceLogs

  const allTenants = useAppSelector(
    (state: RootState) => state?.authUser?.user?.clients
  )
  const selectedTenantId = useAppSelector(
    (state: RootState) => state.authUser.selectedClientTenant?.id
  )
  const tabsList = [...FIXED_TABS]

  // Inbound sms tab not linked to case/patient as of now so hidden for case view
  if (!isCaseView && selectedTenantId !== TENANTS.TEVA) {
    tabsList.push({
      label: correspondenceLogsPage.INBOUND_SMS,
      component: null,
    })
  }
  if (selectedTenantId === TENANTS.TEVA_CARES) {
    tabsList.push({
      label: correspondenceLogsPage.INBOUND_MAIL,
      component: null,
    })
  }

  useEffect(() => {
    if (!isCaseView) {
      navigate(
        `/correspondence/correspondence-logs/outbound/${selectedTenantId}`
      )
    }
  }, [selectedTenantId])

  const allTenantsOptions: DropdownType<DropDownOption>[] = []
  allTenants?.forEach((item: AuthUserClient) => {
    if (item?.name_of_organization !== CORRESPONDENCE_LOGS_STRINGS.GLOBAL) {
      allTenantsOptions.push({
        label: item?.name_of_organization,
        value: item?.id?.toString(),
      })
    }
  })

  const [openPreviewModal, setOpenPreviewModal] = useState<{
    value: boolean
    type: string
  }>({ value: false, type: '' })

  const [refetchKey, setRefetchKey] = useState<number>(0)
  const [refetchTableKey, setRefetchTableKey] = useState<number>(0)

  const activeOutboundColumns = useAppSelector(
    (state: RootState) =>
      state?.tableColumn?.tableColumn?.[
        `correspondenceLogsOutboundTable-${selectedTenantId}`
      ]
  )

  const parsedOutboundActiveColumns = activeOutboundColumns
    ? JSON?.parse(activeOutboundColumns)
    : []

  const activeInboundFaxColumns = useAppSelector(
    (state: RootState) =>
      state?.tableColumn?.tableColumn?.[
        `correspondenceLogsInboundTable-${selectedTenantId}`
      ]
  )
  const parsedInboundFaxActiveColumns = activeInboundFaxColumns
    ? JSON?.parse(activeInboundFaxColumns)
    : []

  const inbounWebCols = useAppSelector(
    (state) =>
      state?.tableColumn?.tableColumn?.[
        `correspondenceLogsInboundWebTable-${selectedTenantId}`
      ]
  )
  const activeInboundSmsColumns = useAppSelector(
    (state: RootState) =>
      state?.tableColumn?.tableColumn?.[
        `correspondenceLogsInboundSMSTable-${selectedTenantId}`
      ]
  )
  const parsedInboundSmsActiveColumns = activeInboundSmsColumns
    ? JSON?.parse(activeInboundSmsColumns)
    : []
  const activeInboundMailColumns = useAppSelector(
    (state: RootState) =>
      state?.tableColumn?.tableColumn?.[
        `correspondenceLogsInboundMailTable-${selectedTenantId}`
      ]
  )
  const parsedInboundMailActiveColumns = activeInboundMailColumns
    ? JSON?.parse(activeInboundMailColumns)
    : []

  const fields = getFields(uniqueKey)

  const tableActions: TableActionType<TransformedDataType>[] = useMemo(() => {
    if (showTableType !== correspondenceLogsPage.OUTBOUND_LOWERCASE) return []

    return [
      {
        key: TABLE_ACTIONS_NAMESPACE.RESEND_LOWERCASE,
        label: NAMESPACES.RESEND_BUTTON,
        type: correspondenceLogsPage.COMPONENT,
        renderActionComponent: (row, rowIndex, onActionClick, isDisabled) => (
          <CustomButton
            isFilled
            onClick={(event) => {
              event.stopPropagation()
              if (!isDisabled)
                onActionClick(
                  TABLE_ACTIONS_NAMESPACE.RESEND_LOWERCASE,
                  row,
                  rowIndex
                )
            }}
            disabled={isDisabled}
            data-qa-id={`resend-btn-${row?.id ?? rowIndex}`}
          >
            {NAMESPACES?.RESEND_BUTTON}
          </CustomButton>
        ),
      },
    ]
  }, [showTableType])

  const OpenModalForPreview = async (
    index: number,
    type: string,
    _row?: TransformedDataType
  ) => {
    if (type !== 'WEB') {
      const data = {
        id: correspondenceFilteredLogsData?.content[index]?.id,
        body: correspondenceFilteredLogsData?.content[index]?.body,
        subject: correspondenceFilteredLogsData?.content[index]?.subject,
        createdAt: correspondenceFilteredLogsData?.content[index]?.created_at,
        createdBy: correspondenceFilteredLogsData?.content[index]?.created_by,
        attachment:
          correspondenceFilteredLogsData?.content[index]?.document_entities
            ?.attachment,
        modifiedAt: correspondenceFilteredLogsData?.content[index]?.modified_at,
        modifiedBy: correspondenceFilteredLogsData?.content[index]?.modified_by,
        viewObject: correspondenceFilteredLogsData?.content[index]?.view_object,
        recipientsCc:
          type === emailTemplate.FAX
            ? correspondenceFilteredLogsData?.content[index]?.fax_recipientCc
            : correspondenceFilteredLogsData?.content[index]?.recipients_cc,
        recipientsTo:
          type === emailTemplate.FAX
            ? correspondenceFilteredLogsData?.content[index]?.fax_recipientTo
            : correspondenceFilteredLogsData?.content[index]?.recipients_to,
        templateName:
          correspondenceFilteredLogsData?.content[index]?.template_name,
        recipientsBcc:
          type === emailTemplate.FAX
            ? correspondenceFilteredLogsData?.content[index]?.fax_recipientBcc
            : correspondenceFilteredLogsData?.content[index]?.recipients_bcc,
        correspondenceType: type,
      }
      setPreviewData({
        data: data,
        documentEntities:
          correspondenceFilteredLogsData?.content[index]?.document_entities
            ?.attachment ?? [],
        type: type,
        communicationId: correspondenceFilteredLogsData?.content[index]?.id,
        faxName: correspondenceFilteredLogsData?.content[index]?.template_name,
        status:
          correspondenceFilteredLogsData?.content[index]?.category_status_id,
        recordId: correspondenceFilteredLogsData?.content[index]?.id,
      })
    }
    if (type === 'WEB') {
      setPreviewData({
        type: type,
        communicationId: _row?.communication_id,
        row: _row,
        status: _row?.status,
        recordId: _row?.id,
      })
    }
  }

  const onFocus = async (
    searchCriteria: SearchCriteria,
    page: number,
    size: number,
    header?: string
  ) => {
    try {
      let response
      if (uniqueKey !== correspondenceLogsPage.INBOUND_WEB_KEY) {
        const modifiedCriteria = [
          {
            search_key: header ? header : searchCriteria.search_key,
            search_value: searchCriteria.search_value,
            operator: 'CONTAINS',
          },
          {
            search_key: 'RECORD_STATUS',
            search_value: ['ACTIVE'],
            operator: 'EQUALS',
          },
          {
            search_key: correspondenceLogsPage.COMMUNICATION_STATUS_KEY,
            search_value: [
              uniqueKey === correspondenceLogsPage.OUTBOUND_LOWERCASE
                ? correspondenceLogsPage.OUTBOUND
                : correspondenceLogsPage.INBOUND,
            ],
            operator: correspondenceLogsPage.EQUALS,
          },
        ]
        const payload = {
          search_criteria: modifiedCriteria,
          page: page - 1,
          size: size,
        }
        response = await filterDataOnScroll({
          key: header ? header : searchCriteria.search_key,
          payload: payload,
        })
        return {
          data: response?.data?.content,
          size: response?.data?.content?.length,
        }
      } else if (
        searchCriteria.search_key ===
        correspondenceLogsPage.STATUS_KEY_UPPERCASE
      ) {
        response = await fetchTypeStatusForCommunications(
          searchKeyToFieldMap[searchCriteria.search_key]
        )
        return {
          data: response?.data
            ?.filter((item: { [key: string]: string }) => {
              const value = item.value?.toLowerCase()
              const search = searchCriteria?.search_value?.[0]
                ?.trim()
                ?.toLowerCase()
              return (
                value &&
                value !== '' &&
                (search === '' || value.includes(search))
              )
            })
            .map((item: { [key: string]: string }) => item.value),
          size: response?.data?.length,
        }
      } else {
        const payload = {
          entity_name: searchKeyToEntityMap[searchCriteria.search_key],
          distinct: true,
          select: [searchKeyToFieldMap[searchCriteria.search_key]],
          filters: [
            {
              criteria: [
                {
                  field_name: searchKeyToFieldMap[searchCriteria.search_key],
                  values: searchCriteria.search_value,
                  operation: searchCriteria.operator,
                },
              ],
            },
          ],
          page: page - 1,
          size: size,
        }

        response = await getEntityDataByEntityNameApiForFilter({
          payload: payload,
        })
      }
      return {
        data: response?.data?.content
          ?.filter((item: { [key: string]: string }) => {
            const value = item[searchKeyToFieldMap[searchCriteria.search_key]]
            return value !== null && value.trim() !== ''
          })
          .map(
            (item: { [key: string]: string }) =>
              item[searchKeyToFieldMap[searchCriteria.search_key]]
          ),
        size: response?.data?.content?.length,
      }
    } catch (err) {
      console.log(err)
      return {
        data: [],
        size: 0,
      }
    }
  }

  const outboundColumns: TableColumnType<TransformedDataType>[] = [
    {
      header: correspondenceLogsPage.COMMUNICATION_ID,
      accessor:
        correspondenceLogsPage.COMMUNICATION_ID_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      type: correspondenceLogsPage.COMPONENT,
      filterData: {
        dataType: 'string',
      },
      renderComponent: (row: TransformedDataType, rowIndex: number) => (
        <CommunicationIdRenderer
          row={row}
          rowIndex={rowIndex}
          onActionClick={onActionClick}
        />
      ),
    },
    ...((!isCaseView
      ? [
          {
            header: correspondenceLogsPage.CASE_ID,
            accessor:
              correspondenceLogsPage.CASE_KEY as keyof TransformedDataType,
            minWidth: '3',
            type: correspondenceLogsPage.COMPONENT,
            isSortable: false,
            filterData: {
              dataType: 'string',
            },
            renderComponent: (row: TransformedDataType, rowIndex: number) => (
              <CaseKeyRenderer
                uniqueKey={uniqueKey}
                row={row}
                rowIndex={rowIndex}
                handleCaseKeyClick={handleCaseKeyClick}
              />
            ),
          },
        ]
      : []) as TableColumnType<TransformedDataType>[]),
    ...((!isCaseView
      ? [
          {
            header: correspondenceLogsPage.CASE_TYPE,
            accessor:
              correspondenceLogsPage.SERVICE_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: false,
            filterData: {
              dataType: 'string',
              convertToUppercase: true,
              onKeyPress: (searchCriteria: SearchCriteria) =>
                onFocus(
                  searchCriteria,
                  1,
                  10,
                  correspondenceLogsPage.CASE_TYPE_UPPERCASE
                )?.then((result) => {
                  return result?.data
                }),
              onFocus: (
                searchCriteria: SearchCriteria,
                page: number,
                size: number
              ) =>
                onFocus(
                  searchCriteria,
                  page,
                  size,
                  correspondenceLogsPage.CASE_TYPE_UPPERCASE
                ),
            },
          },
        ]
      : []) as TableColumnType<TransformedDataType>[]),
    {
      header: correspondenceLogsPage.CHANNEL,
      accessor: correspondenceLogsPage.MODE_KEY as keyof TransformedDataType,
      type: correspondenceLogsPage.COMPONENT,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'string',
        convertToUppercase: true,
        onKeyPress: (searchCriteria: SearchCriteria) =>
          onFocus(
            searchCriteria,
            1,
            10,
            correspondenceLogsPage.MODE_KEY_UPPERCASE
          )?.then((result) => {
            return result?.data
          }),
        onFocus: (searchCriteria: SearchCriteria, page: number, size: number) =>
          onFocus(
            searchCriteria,
            page,
            size,
            correspondenceLogsPage.MODE_KEY_UPPERCASE
          ),
      },
      renderComponent: (row) => (
        <CommunicationMode mode={row?.communication_type as string} />
      ),
    },

    {
      header: correspondenceLogsPage.PROGRAM,
      accessor: correspondenceLogsPage.PROGRAM_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'string',
        convertToUppercase: true,
        onKeyPress: (searchCriteria: SearchCriteria) =>
          onFocus(searchCriteria, 1, 10).then((result) => {
            return result.data
          }),
        onFocus: onFocus,
      },
    },
    {
      header: correspondenceLogsPage.PRODUCT,
      accessor: correspondenceLogsPage.PRODUCT_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'string',
        convertToUppercase: true,
        onKeyPress: (searchCriteria: SearchCriteria) =>
          onFocus(searchCriteria, 0, 10)?.then((result) => {
            return result?.data
          }),
        onFocus: onFocus,
      },
    },
    {
      header: correspondenceLogsPage.RECIPIENT_TYPE,
      accessor:
        correspondenceLogsPage.RECIPIENT_TYPE_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: false,
      filterData: {
        dataType: 'string',
      },
    },
    {
      header: correspondenceLogsPage.RECIPIENT_ID,
      accessor:
        correspondenceLogsPage.RECIPIENT_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: false,
      filterData: {
        dataType: 'string',
      },
    },
    {
      header: correspondenceLogsPage.SENT_TO,
      accessor: correspondenceLogsPage.RECEIVER as keyof TransformedDataType,
      minWidth: '3',
      isSortable: false,
      filterData: {
        dataType: 'string',
        disableOperatorDropdown: true,
      },
    },
    {
      header: correspondenceLogsPage.AGENT_NAME,
      accessor:
        correspondenceLogsPage.AGENT_NAME_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'string',
      },
    },
    {
      header: correspondenceLogsPage.SENDER_NAME,
      accessor:
        correspondenceLogsPage.SENDER_NAME_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'string',
      },
    },
    {
      header: correspondenceLogsPage.SENT_AT,
      accessor:
        correspondenceLogsPage.DATE_AND_TIME_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'date-time',
      },
    },
    {
      header: correspondenceLogsPage.STATUS,
      accessor: correspondenceLogsPage.STATUS_KEY as keyof TransformedDataType,
      minWidth: '3',
      type: correspondenceLogsPage.COMPONENT,
      filterData: {
        dataType: 'string',
        convertToUppercase: true,
        onKeyPress: (searchCriteria: SearchCriteria) =>
          onFocus(
            searchCriteria,
            0,
            10,
            correspondenceLogsPage.STATUS_KEY_UPPERCASE
          )?.then((result) => {
            return result?.data
          }),
        onFocus: (searchCriteria: SearchCriteria, page: number, size: number) =>
          onFocus(
            searchCriteria,
            page,
            size,
            correspondenceLogsPage.STATUS_KEY_UPPERCASE
          ),
      },
      isSortable: true,
      renderComponent: (row: TransformedDataType) => (
        <CommunicationStatus
          approved={row?.status as string}
          statusReason={row?.status_reason as string}
          tableType={showTableType}
          recordId={row?.id?.toString() ?? ''}
          setRefetchKey={setRefetchKey}
        />
      ),
    },
    {
      header: correspondenceLogsPage.REFERENCE_ID,
      accessor:
        correspondenceLogsPage.REFERENCE_ID_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'string',
      },
    },
  ]

  const inboundColumns: TableColumnType<TransformedDataType>[] = [
    {
      header: correspondenceLogsPage.COMMUNICATION_ID,
      accessor:
        correspondenceLogsPage.COMMUNICATION_ID_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      type: correspondenceLogsPage.COMPONENT,
      filterData: {
        dataType: 'string',
      },
      renderComponent: (row: TransformedDataType, rowIndex: number) => (
        <CommunicationIdRenderer
          row={row}
          rowIndex={rowIndex}
          onActionClick={onActionClick}
        />
      ),
    },
    ...((uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE
      ? [
          {
            header: correspondenceLogsPage.MESSAGE,
            accessor:
              correspondenceLogsPage.MESSAGE_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: false,
            filterData: {
              dataType: 'string',
            },
          },
        ]
      : []) as TableColumnType<TransformedDataType>[]),
    ...((!isCaseView
      ? [
          {
            header: correspondenceLogsPage.CASE_ID,
            accessor:
              uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY
                ? (correspondenceLogsPage.CASE_ID_KEY as keyof TransformedDataType)
                : (correspondenceLogsPage.CASE_KEY as keyof TransformedDataType),
            minWidth: '3',
            isSortable: false,
            type: correspondenceLogsPage.COMPONENT,
            renderComponent: (row: TransformedDataType, rowIndex: number) => (
              <CaseKeyRenderer
                uniqueKey={uniqueKey}
                row={row}
                rowIndex={rowIndex}
                handleCaseKeyClick={handleCaseKeyClick}
              />
            ),
            filterData: {
              dataType: 'string',
            },
          },
        ]
      : []) as TableColumnType<TransformedDataType>[]),
    ...((!isCaseView
      ? [
          {
            header: correspondenceLogsPage.CASE_TYPE,
            accessor:
              uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY
                ? (correspondenceLogsPage.CASE_TYPE_KEY as keyof TransformedDataType)
                : (correspondenceLogsPage.SERVICE_KEY as keyof TransformedDataType),
            minWidth: '3',
            isSortable: false,
            filterData: {
              dataType: 'string',
              convertToUppercase: true,
              onKeyPress: (searchCriteria: SearchCriteria) =>
                onFocus(
                  searchCriteria,
                  1,
                  10,
                  correspondenceLogsPage.CASE_TYPE_UPPERCASE
                ).then((result) => {
                  return result.data
                }),
              onFocus: (
                searchCriteria: SearchCriteria,
                page: number,
                size: number
              ) =>
                onFocus(
                  searchCriteria,
                  page,
                  size,
                  correspondenceLogsPage.CASE_TYPE_UPPERCASE
                ),
            },
          },
        ]
      : []) as TableColumnType<TransformedDataType>[]),
    {
      header: correspondenceLogsPage.PROGRAM,
      accessor: correspondenceLogsPage.PROGRAM_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'string',
        convertToUppercase: true,
        onKeyPress: (searchCriteria: SearchCriteria) =>
          onFocus(searchCriteria, 1, 10).then((result) => {
            return result.data
          }),
        onFocus: (searchCriteria: SearchCriteria, page: number, size: number) =>
          onFocus(searchCriteria, page, size),
      },
    },
    {
      header: correspondenceLogsPage.PRODUCT,
      accessor: correspondenceLogsPage.PRODUCT_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'string',
        convertToUppercase: true,
        onKeyPress: (searchCriteria: SearchCriteria) =>
          onFocus(searchCriteria, 1, 10).then((result) => {
            return result.data
          }),
        onFocus: (searchCriteria: SearchCriteria, page: number, size: number) =>
          onFocus(searchCriteria, page, size),
      },
    },
    ...((showTableType === correspondenceLogsPage.INBOUND_WEB_KEY &&
    selectedTenantId === TENANTS.NOVO
      ? [
          {
            header: correspondenceLogsPage.REQUESTOR_TYPE,
            accessor:
              correspondenceLogsPage.REQUESTOR_TYPE_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: true,
            filterData: {
              dataType: 'string',
            },
          },
          {
            header: correspondenceLogsPage.PATIENT_FIRST_NAME,
            accessor:
              correspondenceLogsPage.PATIENT_FIRST_NAME_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: true,
            filterData: {
              dataType: 'string',
            },
          },
          {
            header: correspondenceLogsPage.PATIENT_LAST_NAME,
            accessor:
              correspondenceLogsPage.PATIENT_LAST_NAME_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: true,
            filterData: {
              dataType: 'string',
            },
          },
          {
            header: correspondenceLogsPage.PRESCRIBER_FIRST_NAME,
            accessor:
              correspondenceLogsPage.PRESCRIBER_FIRST_NAME_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: true,
            filterData: {
              dataType: 'string',
            },
          },
          {
            header: correspondenceLogsPage.PRESCRIBER_LAST_NAME,
            accessor:
              correspondenceLogsPage.PRESCRIBER_LAST_NAME_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: true,
            filterData: {
              dataType: 'string',
            },
          },
          {
            header: correspondenceLogsPage.FACILITY_NAME,
            accessor:
              correspondenceLogsPage.FACILITY_NAME_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: true,
            filterData: {
              dataType: 'string',
            },
          },
        ]
      : []) as TableColumnType<TransformedDataType>[]),
    {
      header: correspondenceLogsPage.RECEIVED_FROM,
      accessor:
        uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY
          ? (correspondenceLogsPage.RECEIVED_FROM_KEY_LOWER as keyof TransformedDataType)
          : (correspondenceLogsPage.RECEIVED_FROM_NEW_KEY as keyof TransformedDataType),
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: 'string',
        ...(uniqueKey !== correspondenceLogsPage.INBOUND_WEB_KEY &&
          uniqueKey !== correspondenceLogsPage.INBOUND_MAIL_LOWERCASE && {
            format: FORMAT_PATTERN.PHONE_INPUT,
          }),
      },
    },
    {
      header: correspondenceLogsPage.RECEIVED_AT,
      accessor:
        correspondenceLogsPage.RECEIVED_AT_KEY as keyof TransformedDataType,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType:
          uniqueKey !== correspondenceLogsPage.INBOUND_WEB_KEY
            ? 'date-time'
            : 'date-time',
      },
    },
    ...((uniqueKey !== correspondenceLogsPage.INBOUND_SMS_LOWERCASE
      ? [
          {
            header: correspondenceLogsPage.AGENT_NAME,
            accessor:
              correspondenceLogsPage.AGENT_NAME_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: true,
            filterData: {
              dataType: 'string',
            },
          },
        ]
      : []) as TableColumnType<TransformedDataType>[]),
    {
      header: correspondenceLogsPage.STATUS,
      accessor:
        correspondenceLogsPage.STATUS.toLowerCase() as keyof TransformedDataType,
      minWidth: '3',
      filterData: {
        dataType: 'string',
        convertToUppercase: true,
        onKeyPress: (searchCriteria: SearchCriteria) =>
          onFocus(
            searchCriteria,
            0,
            10,
            correspondenceLogsPage.STATUS_KEY_UPPERCASE
          ).then((result) => {
            return result.data
          }),
        onFocus: (searchCriteria: SearchCriteria, page: number, size: number) =>
          onFocus(
            searchCriteria,
            page,
            size,
            correspondenceLogsPage.STATUS_KEY_UPPERCASE
          ),
      },
      type: correspondenceLogsPage.COMPONENT,
      isSortable: true,
      renderComponent: (row: TransformedDataType) => (
        <CommunicationStatus
          approved={row?.status as string}
          tableType={showTableType}
          recordId={row?.id?.toString() ?? ''}
          setRefetchKey={setRefetchKey}
        />
      ),
    },
    ...((showTableType === correspondenceLogsPage.INBOUND_WEB_KEY &&
    selectedTenantId === TENANTS.NOVO
      ? [
          {
            header: correspondenceLogsPage.QUICK_DENIAL_REASON,
            accessor:
              correspondenceLogsPage.QUICK_DENIAL_REASON_KEY as keyof TransformedDataType,
            minWidth: '3',
            isSortable: true,
            type: correspondenceLogsPage.COMPONENT,
            renderComponent: (row: TransformedDataType) => (
              <QuickDenialMsgViewer
                quickDenialReasonObj={row?.quick_denial_reason_obj as string}
                quickDenialReason={row?.quick_denial_reason as string}
              />
            ),
            filterData: {
              dataType: 'string',
            },
          },
        ]
      : []) as TableColumnType<TransformedDataType>[]),
  ]

  const programPermissionSearchObject = {
    search_key: correspondenceLogsPage.PROGRAM_KEY_UPPERCASE,
    search_value: permissionsData.current?.viewPermissions ?? [],
    operator: filterOperatorValues.EQUALS,
  }

  const getValidKeys = (searchKey: string) => {
    switch (searchKey) {
      case correspondenceLogsPage.CASE_KEY.toUpperCase():
        return correspondenceLogsPage.CASES
      case correspondenceLogsPage.COMMUNICATION_ID_KEY.toUpperCase():
        return correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE
      case correspondenceLogsPage.RECEIVED_FROM_KEY.toUpperCase():
        return correspondenceLogsPage.RECEIVED_FROM_EXPORT_KEY.toUpperCase()
      case correspondenceLogsPage.MODE_KEY.toUpperCase():
        return correspondenceLogsPage.MODE_KEY_UPPERCASE
      case correspondenceLogsPage.STATUS_KEY.toUpperCase():
        return correspondenceLogsPage.STATUS.toUpperCase()
      case correspondenceLogsPage.RECEIVED_AT_KEY.toUpperCase():
        return correspondenceLogsPage.CREATED_AT_KEY.toUpperCase()
      case correspondenceLogsPage.SERVICE_KEY_UPPERCASE:
        return correspondenceLogsPage.CASE_TYPE_KEY.toUpperCase()
      default:
        return searchKey
    }
  }
  const onFilterChange = (value: TableFilterObjectBody) => {
    const newValue = {
      ...value,
      search_criteria: { ...value.search_criteria },
    }

    Object.entries(newValue.search_criteria).forEach(([key, criteria]) => {
      if (!criteria) return

      const searchKey = criteria.search_key
      if (
        searchKey === correspondenceLogsPage.RECEIVED_FROM_UPPERCASE ||
        searchKey === correspondenceLogsPage.SENDER_NAME_UPPERCASE
      ) {
        newValue.search_criteria[key] = {
          ...criteria,
          search_value: criteria.search_value?.map((val) =>
            val
              ?.toString()
              .replace(/^\+1\s?/, '')
              .replace(/[\s\-().]/g, '')
          ),
        }
      }
    })
    if (uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY) {
      const newValue = {
        ...value,
        search_criteria: { ...value.search_criteria },
      }

      Object.entries(newValue?.search_criteria).forEach(([key, criteria]) => {
        if (!criteria) return

        const { operator, search_value } = criteria

        const isEmpty =
          !search_value ||
          search_value.length === 0 ||
          search_value.every((v) => v === '' || v == null)

        if (isEmpty) {
          let updatedOperator = operator

          if (operator === correspondenceLogsPage.IS_EMPTY_NULL) {
            updatedOperator = correspondenceLogsPage.IS_NULL
          } else if (
            operator === correspondenceLogsPage.IS_NOT_EMPTY_NOT_NULL
          ) {
            updatedOperator = correspondenceLogsPage.IS_NOT_NULL
          } else {
            setApiFilter(newValue)
            return
          }

          newValue.search_criteria[key] = {
            ...criteria,
            operator: updatedOperator,
          }
        }
      })
      setApiFilter(newValue)
    } else {
      setApiFilter(newValue)
    }
  }
  const downloadEmailContent = async () => {
    const response = await fetch(staticURL || dynamicURL)
    if (!response?.ok) {
      throw new Error('Failed to fetch the file.')
    }
    let blob
    blob = await response.blob()
    if (blob.type === 'application/octet-stream')
      blob = new Blob([blob], { type: 'application/pdf' })
    const blobUrl = window.URL.createObjectURL(blob)
    const aTag = document.createElement('a')
    aTag.href = blobUrl
    aTag.download = 'file-download'
    document.body.appendChild(aTag)
    aTag.click()
    document.body.removeChild(aTag)
  }
  const handleTabChange = (label: string) => {
    switch (label) {
      case correspondenceLogsPage.OUTBOUND:
        navigate(
          `/correspondence/correspondence-logs/outbound/${selectedTenantId}`
        )
        setDefaultActiveTab({
          label: correspondenceLogsPage.OUTBOUND,
          component: null,
        })
        setSearchFromTableCriterias([])
        setShowTableType(correspondenceLogsPage.OUTBOUND_LOWERCASE)
        setCurrentPage(FIELD_VALUE_CONSTANTS.ONE)
        setRowPerPage(FIELD_VALUE_CONSTANTS.TEN)
        setSizePerScroll(FIELD_VALUE_CONSTANTS.TWENTY)
        setScrollRowPage(FIELD_VALUE_CONSTANTS.ZERO)
        setSortDirection({
          key: correspondenceLogsPage.DATE_AND_TIME_KEY,
          isAsc: SORT_KEYS.Desc,
          keyName: correspondenceLogsPage.DATE_AND_TIME_KEY_CAMELCASE,
          direction: SORT_KEYS.Desc,
        })
        setCorrespondenceLogFormData({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          program: [],
          service: [],
          status: [],
        })
        setPreviewData({
          data: {},
          documentEntities: [],
          type: '',
        })
        setAllOptions({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          status: [],
          service: [],
          program: [],
        })
        setApiFilter({
          fields: null,
          search_criteria: {},
        })
        break
      case correspondenceLogsPage.INBOUND_FAX:
        navigate(
          `/correspondence/correspondence-logs/inbound/${selectedTenantId}`
        )
        setSearchFromTableCriterias([])
        setDefaultActiveTab({
          label,
          component: null,
        })
        setShowTableType(correspondenceLogsPage.INBOUND_LOWERCASE)
        setCurrentPage(FIELD_VALUE_CONSTANTS.ONE)
        setRowPerPage(FIELD_VALUE_CONSTANTS.TEN)
        setSizePerScroll(FIELD_VALUE_CONSTANTS.TWENTY)
        setScrollRowPage(FIELD_VALUE_CONSTANTS.ZERO)
        setSortDirection({
          key: correspondenceLogsPage.DATE_AND_TIME_KEY,
          isAsc: SORT_KEYS.Desc,
          keyName: correspondenceLogsPage.DATE_AND_TIME_KEY_CAMELCASE,
          direction: SORT_KEYS.Desc,
        })
        setCorrespondenceLogFormData({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          program: [],
          service: [],
          status: [],
        })
        setPreviewData({
          data: {},
          documentEntities: [],
          type: '',
        })
        setAllOptions({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          status: [],
          service: [],
          program: [],
        })
        setApiFilter({
          fields: null,
          search_criteria: {},
        })
        break
      case correspondenceLogsPage.INBOUND_SMS:
        navigate(
          `/correspondence/correspondence-logs/inbound-sms/${selectedTenantId}`
        )
        setSearchFromTableCriterias([])
        setDefaultActiveTab({
          label,
          component: null,
        })
        setShowTableType(correspondenceLogsPage.INBOUND_SMS_LOWERCASE)
        setCurrentPage(FIELD_VALUE_CONSTANTS.ONE)
        setRowPerPage(FIELD_VALUE_CONSTANTS.TEN)
        setSizePerScroll(FIELD_VALUE_CONSTANTS.TWENTY)
        setScrollRowPage(FIELD_VALUE_CONSTANTS.ZERO)
        setSortDirection({
          key: correspondenceLogsPage.DATE_AND_TIME_KEY,
          isAsc: SORT_KEYS.Desc,
          keyName: correspondenceLogsPage.DATE_AND_TIME_KEY_CAMELCASE,
          direction: SORT_KEYS.Desc,
        })
        setCorrespondenceLogFormData({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          program: [],
          service: [],
          status: [],
        })
        setPreviewData({
          data: {},
          documentEntities: [],
          type: '',
        })
        setAllOptions({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          status: [],
          service: [],
          program: [],
        })
        setApiFilter({
          fields: null,
          search_criteria: {},
        })
        break

      case correspondenceLogsPage.INBOUND_WEB:
        navigate(
          `/correspondence/correspondence-logs/inbound-web/${selectedTenantId}`
        )
        setSearchFromTableCriterias([])
        setDefaultActiveTab({
          label: correspondenceLogsPage.INBOUND_WEB,
          component: null,
        })
        setShowTableType(correspondenceLogsPage.INBOUND_WEB_KEY)
        setCurrentPage(FIELD_VALUE_CONSTANTS.ONE)
        setRowPerPage(FIELD_VALUE_CONSTANTS.TEN)
        setSizePerScroll(FIELD_VALUE_CONSTANTS.TWENTY)
        setScrollRowPage(FIELD_VALUE_CONSTANTS.ZERO)
        setSortDirection({
          key: correspondenceLogsPage.DATE_AND_TIME_KEY,
          isAsc: SORT_KEYS.Desc,
          keyName: correspondenceLogsPage.DATE_AND_TIME_KEY_CAMELCASE,
          direction: SORT_KEYS.Desc,
        })
        setCorrespondenceLogFormData({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          program: [],
          service: [],
          status: [],
        })
        setPreviewData({
          data: {},
          documentEntities: [],
          type: '',
        })
        setAllOptions({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          status: [],
          service: [],
          program: [],
        })
        setApiFilter({
          fields: null,
          search_criteria: {},
        })
        break
      case correspondenceLogsPage.INBOUND_MAIL:
        navigate(
          `/correspondence/correspondence-logs/inbound-mail/${selectedTenantId}`
        )
        setSearchFromTableCriterias([])
        setDefaultActiveTab({
          label: correspondenceLogsPage.INBOUND_MAIL,
          component: null,
        })
        setShowTableType(correspondenceLogsPage.INBOUND_MAIL_LOWERCASE)
        setCurrentPage(FIELD_VALUE_CONSTANTS.ONE)
        setRowPerPage(FIELD_VALUE_CONSTANTS.TEN)
        setSizePerScroll(FIELD_VALUE_CONSTANTS.TWENTY)
        setScrollRowPage(FIELD_VALUE_CONSTANTS.ZERO)
        setSortDirection({
          key: correspondenceLogsPage.DATE_AND_TIME_KEY,
          isAsc: SORT_KEYS.Desc,
          keyName: correspondenceLogsPage.DATE_AND_TIME_KEY_CAMELCASE,
          direction: SORT_KEYS.Desc,
        })
        setCorrespondenceLogFormData({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          program: [],
          service: [],
          status: [],
        })
        setPreviewData({
          data: {},
          documentEntities: [],
          type: '',
        })
        setAllOptions({
          communication_id: [],
          case_id: [],
          template_name: [],
          communication_type: [],
          status: [],
          service: [],
          program: [],
        })
        setApiFilter({
          fields: null,
          search_criteria: {},
        })
        break
    }
  }
  const formatDropdownOptions = (
    correspondenceOptionsDropdown: { [x: string]: string[] | null }[]
  ) => {
    if (
      !correspondenceOptionsDropdown ||
      !Array.isArray(correspondenceOptionsDropdown)
    )
      return {}
    return correspondenceOptionsDropdown.reduce(
      (
        acc: {
          [key: string]: { label: string; value: string }[]
        },
        item
      ) => {
        const key = Object.keys(item)[0]?.toLowerCase() || ''
        const key1 = Object.keys(item)[0]
        if (key) {
          acc[key] = (item[key1] || []).map((value: string) => ({
            label: value,
            value: value,
          }))
        }
        return acc
      },
      {}
    )
  }
  const getFilterKey = (key: string) => {
    switch (key) {
      case correspondenceLogsPage.COMMUNICATION_ID_KEY:
        return correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE
      case correspondenceLogsPage.CASE_ID_KEY:
        return correspondenceLogsPage.CASES
      default:
        return key?.toUpperCase()
    }
  }
  const safeJsonParse = (value?: string) => {
    try {
      return value ? JSON.parse(value) : null
    } catch {
      return null
    }
  }

  const formatQuickDenialMsg = (
    obj: Record<string, string>,
    separator = '$'
  ) => {
    return Object.values(obj)?.join(separator)
  }

  const transformInboundWebData = (data: InboundWebPayloadType[]) => {
    return data?.map((item: InboundWebPayloadType) => ({
      communication_id: item?.enrollment_id,
      patient_first_name: item?.patient_first_name ?? '',
      patient_last_name: item?.patient_last_name ?? '',
      prescriber_first_name: item?.prescriber_first_name ?? '',
      prescriber_last_name: item?.prescriber_last_name ?? '',
      facility_name_new: item?.facility_name_new ?? '',
      requestor_type: item?.requestor_type ?? '',
      quick_denial_reason:
        formatQuickDenialMsg(
          safeJsonParse(item?.quick_denial_reason ?? '') ?? '',
          ','
        ) ?? '',
      quick_denial_reason_obj:
        formatQuickDenialMsg(
          safeJsonParse(item?.quick_denial_reason ?? '') ?? '',
          '$'
        ) ?? '',
      case_id: item?.c_case?.case_key ?? '',
      case_type: item?.c_case?.services?.service_name ?? '',
      program: item?.hcp_program?.operational_program_name ?? '',
      product: item?.product?.trademarked_name ?? '',
      received_from: item?.received_from ?? '',
      received_at: item?.created_at
        ? formatLocalDateWithZone(item?.created_at as string) ?? '--'
        : null,
      agent_name: `${item?.users?.first_name ?? ''} ${
        item?.users?.first_name && item?.users?.last_name ? '|' : ''
      } ${item?.users?.last_name ?? ''}`.trim(),
      status: item?.enrollment_status?.label ?? '',
      id: item?.id,
    }))
  }
  const filterTableData = async (permissionsArray?: string[]) => {
    setCurrentPage(FIELD_VALUE_CONSTANTS.ONE)
    if (uniqueKey !== correspondenceLogsPage.INBOUND_WEB_KEY) {
      const trim = trimObjects(correspondenceLogFormData)
      const searchCriteria = Object.keys(trim)?.map((key) => ({
        search_key: getFilterKey(key),
        search_value: trim?.[key as keyof CorrespondenceOptions].map((item) =>
          item?.toUpperCase()
        ),
        operator: correspondenceLogsPage.EQUALS,
      }))
      searchCriteria.push({
        search_key: correspondenceLogsPage.PROGRAM_KEY_UPPERCASE,
        search_value:
          permissionsArray ?? permissionsData.current.viewPermissions ?? [],
        operator: filterOperatorValues.EQUALS,
      })
      const payload = {
        fields: fields,
        search_criteria: [
          {
            search_key: correspondenceLogsPage.COMMUNICATION_STATUS_UPPERCASE,
            search_value: [
              uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
              uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE ||
              uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
                ? correspondenceLogsPage.INBOUND
                : correspondenceLogsPage.OUTBOUND,
            ],
            operator: correspondenceLogsPage.EQUALS,
          },
          {
            search_key: correspondenceLogsPage.RECORD_STATUS_KEY,
            search_value: [correspondenceLogsPage.ACTIVE],
            operator: correspondenceLogsPage.EQUALS,
          },
          ...searchCriteria,
          ...searchFromTableCriterias,
        ],
        page: FIELD_VALUE_CONSTANTS.ONE,
        size: rowPerPage,
        sort: !isEmpty(sortDirection?.keyName)
          ? `${sortDirection?.keyName},${sortDirection?.direction}`
          : undefined,
      }

      if (
        uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
        uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE ||
        uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
      ) {
        payload.search_criteria.push({
          search_key: correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE,
          search_value: [
            uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE
              ? searchValues.INBOUND_SMS
              : uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
              ? searchValues.INBOUND_MAIL
              : searchValues.INBOUND_FAX,
          ],
          operator: correspondenceLogsPage.CONTAINS,
        })
      }
      if (isCaseView && caseId) {
        payload.search_criteria.push({
          search_key: correspondenceLogsPage.CASES,
          search_value: [caseId],
          operator: correspondenceLogsPage.EQUALS,
        })
      }
      if (isSuperAdmin || !isEmpty(permissionsData.current))
        dispatch({ type: filterCorrespondenceLogsData.type, payload: payload })
    } else {
      try {
        dispatch(showLoader())
        let modifiedApiFilter = apiFilter
        if (isCaseView && caseId) {
          modifiedApiFilter = {
            ...apiFilter,
            search_criteria: {
              ...apiFilter?.search_criteria,
              CASE_ID: {
                search_key: correspondenceLogsPage.CASE_ID_KEY_UPPERCASE,
                operator: correspondenceLogsPage.EQUALS,
                search_value: [caseId],
              },
            },
          }
        }
        let temporaryObject = Object.entries(
          modifiedApiFilter?.search_criteria ?? {}
        ).map(([_key, item]) => ({
          key: item.search_key,
          value: item.search_value,
          operator: item.operator ?? correspondenceLogsPage.LIKE,
        }))
        temporaryObject = temporaryObject?.map((item) => {
          if (item?.key === correspondenceLogsPage.STATUS.toUpperCase()) {
            return {
              ...item,
              value: [
                item?.value?.toString().toLowerCase().split(' ').join('_'),
              ],
            }
          }
          return item
        })
        if (sortDirection) {
          temporaryObject.push({
            key: sortDirection?.key,
            value: [],
            operator: '',
          })
        }

        const filters = convertToFilters(temporaryObject, keyMap, sortKeyMap)
        const mergedFilter = mergeFilters(filters)
        const payload = buildFilterPayload(mergedFilter)

        const response = await correspondenceInboundWebData(
          payload as Parameters<typeof correspondenceInboundWebData>[0]
        )
        const data = response?.data?.content ?? []
        const numberOfElements = response?.data?.numberOfElements ?? 0
        const totalPages = response?.data?.totalPages ?? 0
        const totalElements = response?.data?.totalElements ?? 0
        setInboundWebPagination({
          numberOfElements: numberOfElements,
          totalPages: totalPages,
          totalElements: totalElements,
        })
        if (data?.length) {
          const transformedData = transformInboundWebData(data)
          setInboundWebTableData(data?.length ? transformedData : [])
        } else {
          setInboundWebTableData([])
        }
      } catch (error) {
        console.error(error)
        setInboundWebTableData([])
      } finally {
        dispatch(hideLoader())
      }
    }
  }
  const getDropdownOptions = async () => {
    const results: { [x: string]: string[] | null }[] = []
    let fieldsToFetch = [...fields]

    try {
      const commIds = await getCaseCommunicationIdByApi(
        uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
          uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE ||
          uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
          ? 'inboundCommunicationIds'
          : 'outboundCommunicationIds'
      )
      if (
        uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
        uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
      ) {
        const caseIds = await getCaseCommunicationIdByApi('inboundCaseIds')

        setAllOptions((prev) => ({
          ...prev,
          id: commIds,
          case_id: caseIds,
        }))
      } else {
        setAllOptions((prev) => ({
          ...prev,
          id: commIds,
        }))
      }
      if (
        uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
        uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
      ) {
        fieldsToFetch = fieldsToFetch.filter(
          (f) =>
            f !== correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE &&
            f !== correspondenceLogsPage.CASE_ID_KEY_UPPERCASE
        )
      } else {
        fieldsToFetch = fieldsToFetch.filter(
          (f) => f !== correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE
        )
      }
    } catch (error) {
      console.error(error)
    }
    const promises = fieldsToFetch?.map(async (item) => {
      const payload = {
        search_criteria: [
          {
            search_key: correspondenceLogsPage.COMMUNICATION_STATUS_KEY,
            search_value: [
              uniqueKey === correspondenceLogsPage.OUTBOUND_LOWERCASE
                ? correspondenceLogsPage.OUTBOUND
                : correspondenceLogsPage.INBOUND,
            ],
            operator: correspondenceLogsPage.EQUALS,
          },
        ],
        page: scrollRowPage,
        size: sizePerScroll,
      }
      if (
        uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
        uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE ||
        uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
      ) {
        payload.search_criteria.push({
          search_key: correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE,
          search_value: [
            uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE
              ? searchValues.INBOUND_SMS
              : uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
              ? searchValues.INBOUND_MAIL
              : searchValues.INBOUND_FAX,
          ],
          operator: correspondenceLogsPage.CONTAINS,
        })
      }
      try {
        const response = await filterDataOnScroll({
          key: item,
          payload: payload,
        })
        results.push({
          [item]: response?.data?.content?.map(
            (item: string) => item.charAt(0) + item.slice(1).toLowerCase()
          ),
        })
      } catch (error) {
        console.error(error)
        results.push({ [item]: null })
      }
    })

    await Promise.all(promises)
    const option = formatDropdownOptions(results)
    setAllOptions((prev) => ({ ...prev, ...option }))
  }
  const handleUserPageChange = async (page: number, rowsPerPage: number) => {
    const trim = trimObjects(correspondenceLogFormData)
    const searchCriteria = Object.keys(trim).map((key) => ({
      search_key: key.toUpperCase(),
      search_value: trim?.[key as keyof CorrespondenceOptions].map((item) =>
        item?.toUpperCase()
      ),
      operator: correspondenceLogsPage.EQUALS,
    }))

    if (uniqueKey !== correspondenceLogsPage.INBOUND_WEB_KEY) {
      searchCriteria?.push({
        search_key: correspondenceLogsPage.RECORD_STATUS_KEY,
        search_value: [correspondenceLogsPage.ACTIVE],
        operator: correspondenceLogsPage.EQUALS,
      })
      if (isCaseView && caseId) {
        searchCriteria?.push({
          search_key: correspondenceLogsPage.CASES,
          search_value: [caseId],
          operator: correspondenceLogsPage.EQUALS,
        })
      }
    }
    if (uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY) {
      try {
        dispatch(showLoader())
        let modifiedApiFilter = apiFilter
        if (isCaseView && caseId) {
          modifiedApiFilter = {
            ...apiFilter,
            search_criteria: {
              ...apiFilter?.search_criteria,
              CASE_ID: {
                search_key: correspondenceLogsPage.CASE_ID_KEY_UPPERCASE,
                operator: correspondenceLogsPage.EQUALS,
                search_value: [caseId],
              },
            },
          }
        }
        let temporaryObject = Object.entries(
          modifiedApiFilter?.search_criteria ?? {}
        ).map(([_key, item]) => ({
          key: item?.search_key,
          value: item?.search_value,
          operator: item?.operator ?? correspondenceLogsPage.LIKE,
        }))
        temporaryObject = temporaryObject?.map((item) => {
          if (item?.key === correspondenceLogsPage.STATUS.toUpperCase()) {
            return {
              ...item,
              value: [
                item?.value?.toString().toLowerCase().split(' ').join('_'),
              ],
            }
          }
          return item
        })
        if (sortDirection) {
          temporaryObject.push({
            key: sortDirection?.key,
            value: [],
            operator: '',
          })
        }

        const filters = convertToFilters(temporaryObject, keyMap, sortKeyMap)
        const mergedFilter = mergeFilters(filters)
        const payload = buildFilterPayload(mergedFilter, rowsPerPage, page - 1)

        const response = await correspondenceInboundWebData(
          payload as Parameters<typeof correspondenceInboundWebData>[0]
        )
        const data = response?.data?.content ?? []
        const numberOfElements = response?.data?.numberOfElements ?? 0
        const totalPages = response?.data?.totalPages ?? 0
        const totalElements = response?.data?.totalElements ?? 0

        setInboundWebPagination({
          numberOfElements: numberOfElements,
          totalPages: totalPages,
          totalElements: totalElements,
        })
        if (data?.length) {
          const transformedData = transformInboundWebData(data)

          setInboundWebTableData(transformedData)
          setCurrentPage(rowsPerPage !== rowPerPage ? 1 : page)
          setRowPerPage(rowsPerPage)
        } else {
          setInboundWebTableData([])
        }
        return
      } catch (error) {
        console.error(error)
        return
      } finally {
        dispatch(hideLoader())
      }
    }
    const data = {
      fields: fields,
      search_criteria: [
        {
          search_key: correspondenceLogsPage.COMMUNICATION_STATUS_UPPERCASE,
          search_value: [
            uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
            uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE ||
            uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
              ? correspondenceLogsPage.INBOUND
              : correspondenceLogsPage.OUTBOUND,
          ],
          operator: correspondenceLogsPage.EQUALS,
        },
        programPermissionSearchObject,
        ...searchCriteria,
        ...searchFromTableCriterias,
      ],
      page: rowsPerPage !== rowPerPage ? 1 : page,
      size: rowsPerPage,
      sort: !isEmpty(sortDirection?.keyName)
        ? `${sortDirection?.keyName},${sortDirection?.direction}`
        : undefined,
    }
    if (
      uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
      uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE ||
      uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
    ) {
      data.search_criteria.push({
        search_key: correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE,
        search_value: [
          uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE
            ? searchValues.INBOUND_SMS
            : uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
            ? searchValues.INBOUND_MAIL
            : searchValues.INBOUND_FAX,
        ],
        operator: correspondenceLogsPage.CONTAINS,
      })
    }

    dispatch({
      type: filterCorrespondenceLogsData.type,
      payload: data,
    })
    setCurrentPage(rowPerPage !== rowsPerPage ? 1 : page)
    setRowPerPage(rowsPerPage)
  }

  //might be used later
  // const getInboundFaxPdf = async (_rowIndex: number) => {
  //   try {
  //     dispatch(showLoader())
  //     setDynamicURL('')
  //     setStaticURL('')
  //     const response = await fetchFaxUrl(
  //       correspondenceFilteredLogsData?.content[_rowIndex]?.id as number
  //     )
  //     if (response) {
  //       dispatch(
  //         addToaster({
  //           message: CORRESPONDENCE_LOGS_STRINGS.DOWNLOAD_SUCCESS_MSG,
  //           type: 'success',
  //           id: uuid(),
  //         })
  //       )
  //       setDynamicURL(response?.data?.url)
  //     }
  //   } catch (error) {
  //     console.error(CORRESPONDENCE_LOGS_STRINGS.API_ERROR, error)
  //     dispatch(
  //       addToaster({
  //         message: CORRESPONDENCE_LOGS_STRINGS.DOWNLOAD_ERROR_MSG,
  //         type: 'error',
  //         id: uuid(),
  //       })
  //     )
  //     setInboundBulk(false)
  //   } finally {
  //     dispatch(hideLoader())
  //   }
  // }

  // const downloadZipFile = async (
  //   rootPath: string,
  //   _rowIndex: number,
  //   documentEntitiesId: number
  // ) => {
  //   if (uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE) {
  //     getInboundFaxPdf(_rowIndex)
  //     setInboundBulk(true)
  //   } else {
  //     try {
  //       if (rootPath) {
  //         dispatch(showLoader())
  //         const response = await axiosInstance.get(
  //           `${API_END_POINT.ENTITY_MANAGER}entities/files/bulk-download/generate-presigned-url?id=${documentEntitiesId}&alias=image`
  //         )
  //         if (response) {
  //           const link = document.createElement('a')
  //           link.href = response.data.url
  //           link.download = 'bulk-download.zip'
  //           document.body.appendChild(link)
  //           link.click()
  //           document.body.removeChild(link)
  //           dispatch(
  //             addToaster({
  //               message: CORRESPONDENCE_LOGS_STRINGS.DOWNLOAD_ZIP_SUCCESS_MSG,
  //               type: 'success',
  //               id: uuid(),
  //             })
  //           )
  //         }
  //       } else {
  //         dispatch(
  //           addToaster({
  //             message: CORRESPONDENCE_LOGS_STRINGS.NO_DOCUMENTS_MSG,
  //             type: 'error',
  //             id: uuid(),
  //           })
  //         )
  //       }
  //     } catch (_error) {
  //       dispatch(
  //         addToaster({
  //           message: CORRESPONDENCE_LOGS_STRINGS.DOWNLOAD_ZIP_ERROR_MSG,
  //           type: 'error',
  //           id: uuid(),
  //         })
  //       )
  //       console.error(_error)
  //     } finally {
  //       dispatch(hideLoader())
  //     }
  //   }
  // }
  const fetchAllEntities = async () => {
    try {
      if (isEmpty(entityObject)) {
        dispatch(showLoader())
        const response = await getAllEntities()
        if (response?.data) {
          const entities = response?.data
          const caseEntity = entities?.find(
            (entity: IEntityType) => entity.name === 'case'
          )
          if (caseEntity) {
            dispatch(
              setLocalEntityObject({
                case: {
                  name: caseEntity?.name,
                  entityId: caseEntity?.id,
                  type: caseEntity?.type,
                },
              })
            )
            dispatch(hideLoader())
            return {
              name: caseEntity?.name,
              entityId: caseEntity?.id,
              type: caseEntity?.type,
            }
          }
        }
      }
    } catch (err) {
      console.error('error : ', err)
      dispatch(hideLoader())
    }
  }
  const getKeysByUniqueKey = (
    uniqueKey: string,
    key: string,
    row: TransformedDataType
  ) => {
    switch (key) {
      case correspondenceLogsPage.SERVICE_KEY:
        return uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY
          ? row?.case_type
          : row?.service
      case correspondenceLogsPage.CASE_ID_KEY:
        return uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY
          ? row?.case_id
          : row?.case_key
      default:
        return ''
    }
  }
  const handleCaseKeyClick = async (
    uniqueKey: string,
    _row: TransformedDataType,
    _rowIndex: number
  ) => {
    const case_id = getKeysByUniqueKey(
      uniqueKey,
      correspondenceLogsPage.CASE_ID_KEY,
      _row
    )
    const service = getKeysByUniqueKey(
      uniqueKey,
      correspondenceLogsPage.SERVICE_KEY,
      _row
    )
    if (case_id) {
      try {
        const caseEntity = !isEmpty(entityObject?.case)
          ? entityObject.case
          : await fetchAllEntities()
        if (!caseEntity) return
        dispatch(showLoader())
        const filterObject = {
          entity_name: caseEntity.name,
          select: [ENTITY_IMPORTANT_KEYS.ID, ENTITY_IMPORTANT_KEYS.CASE_KEY],
          filters: [
            {
              logical_operator: 'AND',
              criteria: [
                {
                  values: [case_id ?? ''],
                  operation: 'EQUALS',
                  field_name: ENTITY_IMPORTANT_KEYS.CASE_KEY,
                },
              ],
            },
          ],
        }

        const caseIdObject = await getGenericEntityListingAPI(filterObject)
        let serviceObject
        if (service?.length && !isEmpty(caseIdObject))
          serviceObject = await getServiceByServiceName(service ?? '')
        if (!isEmpty(caseIdObject) && !isEmpty(serviceObject?.data)) {
          await handleOpenLink(
            caseIdObject?.[0]?.id,
            caseEntity?.entityId,
            serviceObject?.data?.id
          )
          dispatch(hideLoader())
        } else {
          dispatch(hideLoader())
        }
      } catch (err) {
        console.error(err)
        dispatch(hideLoader())
      }
    }
  }

  const buildFilterPayload = (
    filterConfig: FilterEntry[],
    rowsPerPage?: number,
    page?: number
  ): FinalPayload => {
    const basePayload: FinalPayload = {
      entity_name: 'hcp_portal_entity',

      select: [
        'id',
        'patient_name',
        'patient_first_name',
        'patient_last_name',
        'prescriber_first_name',
        'prescriber_last_name',
        'facility_name_new',
        'requestor_type',
        'quick_denial_reason',
        'facility_name',
        'patient_dob',
        'facility_city',
        'hcp_program',
        'enrollment_id',
        'c_case',
        'users',
        'state',
        'received_from',
        'enrollment_status',
        'created_at',
        'product',
      ],

      filters: [],

      related_entities: {
        hcp_program: {
          select: ['id', 'operational_program_name'],
        },
        users: {
          select: ['id'],
        },
        c_case: {
          select: ['services'],
        },
      },

      page: page ?? FIELD_VALUE_CONSTANTS.ZERO,

      size: rowsPerPage ?? rowPerPage,
    }

    const normalizeValues = (
      value: string | string[] | undefined
    ): string[] | undefined => {
      if (Array.isArray(value)) return value
      if (typeof value === 'string') {
        return value.includes(',')
          ? value?.split(',')?.map((v) => v?.trim())
          : [value]
      }
    }

    const buildRecursive = (
      parts: string[],

      value: string | string[] | undefined,

      container: RelatedEntity | FinalPayload,

      operator: string | undefined,

      direction: string | undefined
    ): void => {
      const [current, ...rest] = parts

      if (rest.length === 0) {
        const filterBlock: FilterBlock = {
          criteria: [
            {
              field_name: current,

              ...(operator && { operation: operator }),

              values: normalizeValues(value),

              ...(direction && { direction }),
            },
          ],
        }

        if (!container.filters) {
          container.filters = [filterBlock]
        } else {
          container.filters.push(filterBlock)
        }

        if (!container.select) {
          container.select = []
        }
        if (!container.select.includes(current)) {
          container.select.push(current)
        }
      } else {
        if (!container.related_entities) {
          container.related_entities = {}
        }

        if (!container.select) {
          container.select = []
        }
        if (!container.select.includes(current)) {
          container.select.push(current)
        }

        if (!container.related_entities[current]) {
          container.related_entities[current] = {
            select: [],
          }
        }

        const nextEntity = container.related_entities[current]
        buildRecursive(rest, value, nextEntity, operator, direction)
      }
    }

    for (const { field_name, values, operation, direction } of filterConfig) {
      const parts = field_name.split('.')

      if (parts.length === 1) {
        const topLevelFilter: FilterBlock = {
          criteria: [
            {
              field_name: parts[0],

              ...(operation && { operation: operation }),

              values: values ?? [],

              ...(direction && { direction }),
            },
          ],
        }

        basePayload.filters.push(topLevelFilter)

        if (!basePayload.select.includes(parts[0])) {
          basePayload.select.push(parts[0])
        }
      } else {
        buildRecursive(parts, values, basePayload, operation, direction)
      }
    }

    return basePayload
  }

  const onActionClick = (
    actionKeyName: string,
    _row: TransformedDataType,
    _rowIndex: number
  ) => {
    if (actionKeyName === TABLE_ACTIONS_NAMESPACE.RESEND_LOWERCASE) {
      resendCommunication(_row?.id ?? -1, setRefetchTableKey)
      return
    }
    if (actionKeyName === TABLE_ACTIONS_NAMESPACE.VIEW.toLowerCase()) {
      const mode = _row?.communication_type as string
      if (mode === emailTemplate.EMAIL)
        OpenModalForPreview(_rowIndex, emailTemplate.EMAIL)
      else if (mode === emailTemplate.LETTER)
        OpenModalForPreview(_rowIndex, emailTemplate.LETTER)
      else if (mode === emailTemplate.SMS)
        OpenModalForPreview(_rowIndex, emailTemplate.SMS)
      else if (mode === emailTemplate.FAX)
        OpenModalForPreview(_rowIndex, emailTemplate.FAX)
      else if (mode === emailTemplate.POSTALMAIL)
        OpenModalForPreview(_rowIndex, emailTemplate.POSTALMAIL)
    }
    if (uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY) {
      OpenModalForPreview(_rowIndex, emailTemplate.WEB, _row)
    }
  }
  const findSortKey = (key: string) => {
    switch (key) {
      case correspondenceLogsPage.CASE_KEY:
        return `${correspondenceLogsPage.CASES.toLowerCase()}.${
          correspondenceLogsPage.CASE_KEY
        }`
      case correspondenceLogsPage.PRODUCT_KEY:
        return correspondenceLogsPage.PRODUCT_EXPORT_KEY
      case correspondenceLogsPage.SERVICE_KEY:
        return correspondenceLogsPage.SERVICE_SORT_KEY
      case correspondenceLogsPage.MODE_KEY:
        return correspondenceLogsPage.MODE_KEY_LOWERCASE
      case correspondenceLogsPage.STATUS_KEY:
      case correspondenceLogsPage.STATUS.toLowerCase():
        return `${NAMESPACES.STATUS_KEY}`
      case correspondenceLogsPage.PROGRAM.toLowerCase():
        return correspondenceLogsPage.PROGRAM_KEY
      case correspondenceLogsPage.RECIPIENT_TYPE.toLowerCase()
        .split(' ')
        .join('_'):
        return correspondenceLogsPage.RECIPIENT_TYPE_SORT_KEY
      case correspondenceLogsPage.RECIPIENT.toLowerCase():
        return correspondenceLogsPage.RECIPIENT_SORT_KEY
      case correspondenceLogsPage.RECEIVED_AT_KEY:
        return correspondenceLogsPage.CREATED_AT_KEY
      case correspondenceLogsPage.AGENT_NAME_KEY:
        return correspondenceLogsPage.ASSIGNED_TO_SORT_KEY
      default:
        return key
    }
  }
  const onSortClick = (key: string, isAsc: SORT_KEYS) => {
    if (key) {
      const newKeyName = convertToCamelCase(findSortKey(key))
      setSortDirection({
        keyName: isAsc === SORT_KEYS.Disabled ? '' : newKeyName,
        direction: isAsc,
        key: isAsc === SORT_KEYS.Disabled ? '' : key,
        isAsc: isAsc,
      })
    }
  }
  const handlePreviewClose = (value: boolean, type: string) => {
    setOpenPreviewModal({ value: value, type: type })
    setPreviewData({ data: {}, documentEntities: [], type: '' })
  }

  // can be used later
  // const handleDateReset = () => {
  //   setValues(['', ''])
  //   setCalenderDates(['', ''])
  //   const payload = {
  //     fields: fields,
  //     search_criteria: [
  //       {
  //         search_key: correspondenceLogsPage.COMMUNICATION_STATUS_UPPERCASE,
  //         search_value: [
  //           uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE
  //             ? correspondenceLogsPage.INBOUND
  //             : correspondenceLogsPage.OUTBOUND,
  //         ],
  //         operator: correspondenceLogsPage.EQUALS,
  //       },
  //       programPermissionSearchObject,
  //       ...searchFromTableCriterias,
  //     ],
  //     page: currentPage,
  //     size: rowPerPage,
  //     sort: !isEmpty(sortDirection?.keyName)
  //       ? `${sortDirection?.keyName},${sortDirection?.direction}`
  //       : undefined,
  //   }
  //   dispatch({ type: filterCorrespondenceLogsData.type, payload: payload })
  // }

  // can be used later
  // const handleDateChange = (date: DateObject[]) => {
  //   if (date?.length !== 2) return
  //   const startDate = `${date[0]?.year}-${String(
  //     date[0]?.month?.number
  //   ).padStart(2, '0')}-${String(date[0]?.day).padStart(2, '0')}`
  //   const startValueDate = `${date[0]?.month?.number}/${date[0]?.day}/${date[0]?.year}`
  //   const endDate = `${date[1]?.year}-${String(date[1]?.month?.number).padStart(
  //     2,
  //     '0'
  //   )}-${String(date[1]?.day).padStart(2, '0')}`
  //   const endValueDate = `${date[1]?.month?.number}/${date[1]?.day}/${date[1]?.year}`
  //   setValues([startValueDate, endValueDate])
  //   setCalenderDates([startDate, endDate])
  //   const trim = trimObjects(correspondenceLogFormData)
  //   const searchCriteria = Object.keys(trim).map((key) => ({
  //     search_key:
  //       key.toUpperCase() ===
  //       correspondenceLogsPage.COMMUNICATION_ID_KEY.toUpperCase()
  //         ? correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE
  //         : key.toUpperCase(),
  //     search_value: trim?.[key as keyof CorrespondenceOptions].map((item) =>
  //       item?.toUpperCase()
  //     ),
  //     operator: correspondenceLogsPage.EQUALS,
  //   }))
  //   const payload = {
  //     fields: fields,
  //     search_criteria: [
  //       ...(startDate !== '' && endDate !== ''
  //         ? [
  //             {
  //               search_key: correspondenceLogsPage.DATE_AND_TIME_KEY_UPPERCASE,
  //               search_value: [startDate, endDate].map((date, index) =>
  //                 index === 1 ? `${date} 23:59:59` : `${date} 00:00:00`
  //               ),
  //               operator: correspondenceLogsPage.BETWEEN,
  //             },
  //           ]
  //         : []),
  //       {
  //         search_key: correspondenceLogsPage.COMMUNICATION_STATUS_UPPERCASE,
  //         search_value: [
  //           uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE
  //             ? correspondenceLogsPage.INBOUND
  //             : correspondenceLogsPage.OUTBOUND,
  //         ],
  //         operator: correspondenceLogsPage.EQUALS,
  //       },
  //       programPermissionSearchObject,
  //       ...searchFromTableCriterias,
  //       ...searchCriteria,
  //     ],
  //     page: currentPage,
  //     size: rowPerPage,
  //     sort: !isEmpty(sortDirection?.keyName)
  //       ? `${sortDirection?.keyName},${sortDirection?.direction}`
  //       : undefined,
  //   }
  //   dispatch({ type: filterCorrespondenceLogsData.type, payload: payload })
  // }

  const buildUiDataMappings = (
    columns: InboundColumnType[]
  ): UiDataMappingsType => {
    const mappings = Object.fromEntries(
      columns.map(({ accessor, header }) => {
        if (accessor === 'received_at') {
          return ['created_at', { alias: [header] }]
        }
        return [accessor, { alias: [header] }]
      })
    )
    return {
      ...mappings,
      [keyMap.COMMUNICATION_ID]: { alias: ['Communication Id'] },
      [keyMap.RECEIVED_FROM]: { alias: ['Received From'] },
    }
  }

  const onExportClick = async (
    extension: ExportExtensionType
  ): Promise<void> => {
    const hasAccessor = (col: Column): col is TableColumn => 'accessor' in col
    const isVisible = (col: Column): col is TableColumn =>
      hasAccessor(col) && !col.isHidden
    const parsedCols =
      (inbounWebCols && (JSON.parse(inbounWebCols) as Column[])) || []

    let temporaryObject = Object.entries(apiFilter.search_criteria ?? {}).map(
      ([_key, item]) => ({
        key: item.search_key,
        value: item.search_value,
        operator: item.operator ?? correspondenceLogsPage.LIKE,
      })
    )
    temporaryObject = temporaryObject?.map((item) => {
      if (item?.key === correspondenceLogsPage.STATUS.toUpperCase()) {
        const normalizedStatusValues = (
          Array.isArray(item?.value) ? item.value : [item?.value]
        )
          .filter(
            (value) => value !== undefined && value !== null && value !== ''
          )
          .map((value) => value.toString().toLowerCase().split(' ').join('_'))

        return {
          ...item,
          value: normalizedStatusValues,
        }
      }
      return item
    })
    if (sortDirection) {
      temporaryObject.push({
        key: sortDirection?.key,
        value: [],
        operator: 'LIKE',
      })
    }

    const filters = convertToFilters(temporaryObject, keyMap, sortKeyMap)
    const mergedFilter = mergeFilters(filters)
    const related_entities: Record<string, RelatedEntityType> = Object.keys(
      RELATED_ENTITIES
    ).reduce(
      (acc, key) => {
        const filters = filteredPayload.related_entities?.[key]?.filters

        if (filters !== undefined) {
          acc[key] = {
            ...acc[key],
            filters,
          }
        }

        return acc
      },
      {
        ...RELATED_ENTITIES,
      }
    )

    if (related_entities['c_case']) {
      related_entities['c_case'] = {
        ...related_entities['c_case'],
        select: ['services', 'case_key'],
        related_entities: {
          services: {
            select: ['service_name'],
            ui_data_mappings: {
              service_name: {
                alias: ['Case Type'],
              },
            },
          },
        },
      }
    }

    const nestedFilters: Record<string, RelatedEntityType> = {}
    const filtersToRemove: string[] = []
    const relatedEntityFiltersMap: Record<string, FilterEntry[]> = {}

    mergedFilter.forEach((filter) => {
      if (filter.field_name.includes('.')) {
        const parts = filter.field_name.split('.')

        if (parts.length === 3) {
          const [parentEntity, childEntity, fieldName] = parts

          if (parentEntity === 'c_case' && childEntity === 'services') {
            if (!nestedFilters[parentEntity]) {
              nestedFilters[parentEntity] = {
                ...related_entities[parentEntity],
                select: ['services'],
                related_entities: {},
              }
            }

            const parent = nestedFilters[parentEntity]
            const parentRelatedEntities = parent.related_entities as
              | Record<string, RelatedEntityType>
              | undefined
            if (!parentRelatedEntities) {
              parent.related_entities = {}
            }
            const relatedEntitiesObj = parent.related_entities as Record<
              string,
              RelatedEntityType
            >
            if (!relatedEntitiesObj[childEntity]) {
              relatedEntitiesObj[childEntity] = {
                select: [fieldName],
                ui_data_mappings: {
                  [fieldName]: {
                    alias: ['Case Type'],
                  },
                },
                filters: [],
              }
            }

            const child = relatedEntitiesObj[childEntity]
            const childFilters = child.filters as FilterBlock[] | undefined
            if (child && !childFilters) {
              child.filters = []
            }
            const filters = child.filters as FilterBlock[] | undefined
            if (filters) {
              filters.push({
                criteria: [
                  {
                    field_name: fieldName,
                    operation: filter.operation,
                    values: filter.values,
                  },
                ],
              })
            }

            filtersToRemove.push(filter.field_name)
          }
        } else if (parts.length === 2) {
          const [entityName, fieldName] = parts

          if (related_entities[entityName]) {
            if (!relatedEntityFiltersMap[entityName]) {
              relatedEntityFiltersMap[entityName] = []
            }

            relatedEntityFiltersMap[entityName].push({
              field_name: fieldName,
              operation: filter.operation,
              values: filter.values,
              direction: filter.direction,
            })

            filtersToRemove.push(filter.field_name)
          }
        }
      }
    })

    Object.keys(nestedFilters).forEach((key) => {
      related_entities[key] = nestedFilters[key]
    })

    Object.keys(relatedEntityFiltersMap).forEach((entityName) => {
      if (
        related_entities[entityName] &&
        relatedEntityFiltersMap[entityName].length > 0
      ) {
        related_entities[entityName].filters = [
          { criteria: relatedEntityFiltersMap[entityName] },
        ]
      }
    })

    if (related_entities['services']) {
      delete related_entities['services']
    }

    const relatedEntitiesWithFilters = Object.keys(related_entities).filter(
      (key) => related_entities[key]?.filters
    )

    const cleanedFilter = mergedFilter.filter((filter) => {
      if (filtersToRemove.includes(filter.field_name)) {
        return false
      }

      if (filter.field_name.includes('.')) {
        const entityPrefix = filter.field_name.split('.')[0]
        return !relatedEntitiesWithFilters.includes(entityPrefix)
      }
      return true
    })

    const uiMappings = buildUiDataMappings(inboundColumns)
    if (uiMappings['case_type']) {
      delete uiMappings['case_type']
    }
    if (uiMappings['case_id']) {
      delete uiMappings['case_id']
    }

    const selectFields = parsedCols
      ? Array.from(
          new Set(
            parsedCols
              .filter(isVisible)
              .map((col) => ACCESSOR_EXPORT_MAP[col.accessor])
          )
        )
      : EXPORT_SELECT_FIELDS

    const payload: ExportPayloadType = {
      entity_name: QUEUE_STRINGS.HCP_PORTAL_ENTITY_LOWERCASE,
      select: selectFields,
      related_entities: related_entities as RelatedEntityType,
      ui_data_mappings: uiMappings,
      filters: [{ criteria: cleanedFilter }],
      exclude_fields: ['record_id', 'entity_type', 'category'],
    }
    dispatch(showLoader())
    try {
      const { data } = await exportEntityDataByEntityIdLayoutIdApiForFilter(
        payload
      )
      const fileName =
        extension === 'EXCEL'
          ? 'correspondence_logs.xls'
          : 'correspondence_logs.csv'

      downloadCSV(data, fileName)
      dispatch(
        addToaster({
          id: uniqueId(),
          message: NAMESPACES.EXPORT_SUCCESSFUL,
          type: 'success',
        })
      )
    } catch (error: unknown) {
      let message = NAMESPACES.EXPORT_ERROR
      let type: ToasterTypeValues = ToasterType.ERROR

      if (isAxiosError(error)) {
        const backendMessage = error.response?.data?.message
        const statusCode = error.response?.status

        if (statusCode === HTTP_STATUS_CONSTANTS.CONFLICT && backendMessage) {
          const parsed = parseErrorMessage(backendMessage)

          if (parsed.type === NAMESPACES.WARN) {
            message = parsed.message
            type = ToasterType.WARNING
          }
        }
      }
      dispatch(
        addToaster({
          id: uniqueId(),
          message,
          type,
        })
      )
      console.log(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const onExportOption = async (item: ExportExtensionType) => {
    if (uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY) {
      onExportClick(item)
      return
    }

    const hasAccessor = (col: Column): col is TableColumn => 'accessor' in col
    const isVisible = (col: Column): col is TableColumn =>
      hasAccessor(col) && !col.isHidden

    const parsedCols =
      uniqueKey === correspondenceLogsPage.OUTBOUND_LOWERCASE
        ? parsedOutboundActiveColumns
        : uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE
        ? parsedInboundSmsActiveColumns
        : uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
        ? parsedInboundMailActiveColumns
        : parsedInboundFaxActiveColumns

    const temporaryObject = Object.entries(apiFilter.search_criteria ?? {}).map(
      ([_key, item]) => ({
        key: item.search_key,
        value: item.search_value,
        operator: item.operator ?? correspondenceLogsPage.LIKE,
      })
    )

    temporaryObject.push({
      key: 'COMMUNICATION_STATUS',
      value: [
        uniqueKey === correspondenceLogsPage.OUTBOUND_LOWERCASE
          ? 'Outbound'
          : 'Inbound',
      ],
      operator: 'EQUALS',
    })

    if (uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE) {
      temporaryObject.push({
        key: 'COMMUNICATION_TYPE',
        value: ['SMS'],
        operator: 'EQUALS',
      })
    }

    if (uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE) {
      temporaryObject.push({
        key: 'COMMUNICATION_TYPE',
        value: ['Fax'],
        operator: 'EQUALS',
      })
    }
    if (uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE) {
      temporaryObject.push({
        key: 'COMMUNICATION_TYPE',
        value: ['MAIL'],
        operator: 'EQUALS',
      })
    }

    if (sortDirection?.key) {
      temporaryObject.push({
        key: sortDirection.key,
        value: [],
        operator: 'LIKE',
      })
    }

    const filters = convertToFilters(
      temporaryObject,
      communicationKeyMap,
      communicationSortKeyMap
    )
    const mergedFilter = mergeFilters(filters)

    const related_entities: Record<string, RelatedEntityType> = {
      products: {
        ui_data_mappings: {
          trademarked_name: { alias: ['Product'] },
        },
        exclude_fields: ['record_id', 'entity_type', 'category'],
      },
      users: {
        ui_data_mappings: {
          first_name: { alias: ['Agent First Name'] },
          last_name: { alias: ['Agent Last Name'] },
        },
        exclude_fields: ['record_id', 'entity_type', 'category'],
      },
      view_communication_recipient: {
        select: ['record_key', 'category'],
        ui_data_mappings: {
          record_key: { alias: ['Recipient ID'] },
          category: { alias: ['Recipient Type'] },
        },
        exclude_fields: ['record_id', 'entity_type'],
      },
      programs: {
        select: ['operational_program_name'],
        ui_data_mappings: {
          operational_program_name: { alias: ['Program'] },
        },
        exclude_fields: ['record_id', 'entity_type', 'category'],
      },
      cases: {
        ui_data_mappings: {
          case_key: { alias: ['Case ID'] },
        },
        exclude_fields: ['record_id', 'entity_type', 'category'],
      },
      category_status: {
        ui_data_mappings: {
          label: { alias: ['Status'] },
        },
        exclude_fields: ['record_id', 'entity_type', 'category'],
      },
    }

    const relatedEntityFiltersMap: Record<string, FilterEntry[]> = {}
    const filtersToRemove: string[] = []
    const serviceFilters: FilterEntry[] = []

    mergedFilter.forEach((filter) => {
      if (
        filter.field_name === EXPORT_FIELD_NAMES.SERVICE ||
        filter.field_name === EXPORT_FIELD_NAMES.SERVICES
      ) {
        serviceFilters.push({
          field_name: EXPORT_FIELD_NAMES.SERVICE_NAME,
          operation: filter.operation,
          values: filter.values,
          ...(filter.direction && { direction: filter.direction }),
        })
        filtersToRemove.push(filter.field_name)
      } else if (filter.field_name.includes('.')) {
        const parts = filter.field_name.split('.')
        if (parts.length === 2) {
          const [entityName, fieldName] = parts

          if (related_entities[entityName]) {
            if (!relatedEntityFiltersMap[entityName]) {
              relatedEntityFiltersMap[entityName] = []
            }

            relatedEntityFiltersMap[entityName].push({
              field_name: fieldName,
              operation: filter.operation,
              values: filter.values,
              ...(filter.direction && { direction: filter.direction }),
            })

            filtersToRemove.push(filter.field_name)
          }
        }
      }
    })

    if (related_entities[EXPORT_FIELD_NAMES.CASES]) {
      related_entities[EXPORT_FIELD_NAMES.CASES] = {
        ...related_entities[EXPORT_FIELD_NAMES.CASES],
        select: [EXPORT_FIELD_NAMES.SERVICES, EXPORT_FIELD_NAMES.CASE_KEY],
        related_entities: {
          services: {
            select: [],
            ui_data_mappings: {
              [EXPORT_FIELD_NAMES.SERVICE_NAME]: {
                alias: [ENTITY_ALIASES.CASE_TYPE],
              },
            },
            ...(serviceFilters.length > 0 && {
              filters: [{ criteria: serviceFilters }],
            }),
            exclude_fields: EXCLUDE_FIELDS_STANDARD,
          },
        },
      }
    }

    Object.keys(relatedEntityFiltersMap).forEach((entityName) => {
      if (related_entities[entityName]) {
        related_entities[entityName].filters = [
          { criteria: relatedEntityFiltersMap[entityName] },
        ]
      }
    })

    const cleanedFilter = mergedFilter.filter(
      (filter) => !filtersToRemove.includes(filter.field_name)
    )

    const uiMappings: UiDataMappingsType =
      uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
      uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
        ? COMMUNICATION_UI_MAPPINGS_INBOUND_FAX
        : uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE
        ? COMMUNICATION_UI_MAPPINGS_INBOUND_SMS
        : COMMUNICATION_UI_MAPPINGS

    const selectFields: string[] = parsedCols
      ? Array.from(
          new Set(
            parsedCols
              .filter(isVisible)
              .flatMap((col: { accessor: string }) => {
                const accessor = col.accessor
                if (accessor === COMMUNICATION_ACCESSOR_KEYS.COMMUNICATION_ID)
                  return COMMUNICATION_SELECT_FIELDS.COMMUNICATION_ID
                if (accessor === COMMUNICATION_ACCESSOR_KEYS.CASE_KEY)
                  return COMMUNICATION_SELECT_FIELDS.CASES
                if (accessor === COMMUNICATION_ACCESSOR_KEYS.SERVICE) return ''
                if (accessor === COMMUNICATION_ACCESSOR_KEYS.PROGRAM)
                  return COMMUNICATION_SELECT_FIELDS.PROGRAMS
                if (accessor === COMMUNICATION_ACCESSOR_KEYS.PRODUCT)
                  return COMMUNICATION_SELECT_FIELDS.PRODUCTS
                if (
                  accessor === COMMUNICATION_ACCESSOR_KEYS.RECIPIENT_TYPE ||
                  accessor === COMMUNICATION_ACCESSOR_KEYS.RECIPIENT
                )
                  return COMMUNICATION_SELECT_FIELDS.VIEW_COMMUNICATION_RECIPIENT
                if (accessor === COMMUNICATION_ACCESSOR_KEYS.RECEIVER)
                  return [
                    COMMUNICATION_SELECT_FIELDS.FAX_RECIPIENTTO,
                    COMMUNICATION_SELECT_FIELDS.RECIPIENTS_TO,
                  ]
                if (accessor === COMMUNICATION_ACCESSOR_KEYS.AGENT_NAME)
                  return COMMUNICATION_SELECT_FIELDS.USERS
                if (accessor === COMMUNICATION_ACCESSOR_KEYS.SENDER_NAME)
                  return COMMUNICATION_SELECT_FIELDS.SENDER_NAME
                if (
                  accessor === COMMUNICATION_ACCESSOR_KEYS.CREATED_AT ||
                  accessor === COMMUNICATION_ACCESSOR_KEYS.RECEIVED_AT
                )
                  return COMMUNICATION_SELECT_FIELDS.CREATED_AT
                if (
                  accessor === COMMUNICATION_ACCESSOR_KEYS.CATEGORY_STATUS_ID ||
                  accessor === COMMUNICATION_ACCESSOR_KEYS.STATUS
                )
                  return COMMUNICATION_SELECT_FIELDS.CATEGORY_STATUS
                if (
                  accessor === COMMUNICATION_ACCESSOR_KEYS.CATEGORY_TYPE_ID ||
                  accessor ===
                    COMMUNICATION_ACCESSOR_KEYS.CATEGORY_COMMUNICATION_TYPE_ID
                )
                  return COMMUNICATION_SELECT_FIELDS.TYPE
                return accessor
              })
              .filter((field: string) => field !== '')
          )
        )
      : [
          COMMUNICATION_SELECT_FIELDS.COMMUNICATION_ID,
          COMMUNICATION_SELECT_FIELDS.CASES,
          COMMUNICATION_SELECT_FIELDS.TYPE,
          COMMUNICATION_SELECT_FIELDS.PROGRAMS,
          COMMUNICATION_SELECT_FIELDS.PRODUCTS,
          COMMUNICATION_SELECT_FIELDS.FAX_RECIPIENTTO,
          COMMUNICATION_SELECT_FIELDS.RECIPIENTS_TO,
          COMMUNICATION_SELECT_FIELDS.VIEW_COMMUNICATION_RECIPIENT,
          COMMUNICATION_SELECT_FIELDS.USERS,
          COMMUNICATION_SELECT_FIELDS.SENDER_NAME,
          COMMUNICATION_SELECT_FIELDS.CREATED_AT,
          COMMUNICATION_SELECT_FIELDS.CATEGORY_STATUS,
        ]

    const payload: ExportPayloadType = {
      entity_name: 'communication',
      select: selectFields,
      related_entities: related_entities as RelatedEntityType,
      ui_data_mappings: uiMappings,
      filters: [{ criteria: cleanedFilter }],
      exclude_fields: ['record_id', 'entity_type', 'category'],
    }

    try {
      dispatch(showLoader())
      const { data } = await exportEntityDataByEntityIdLayoutIdApiForFilter(
        payload
      )

      const fileName =
        item === 'EXCEL' ? 'correspondence_logs.xls' : 'correspondence_logs.csv'

      downloadCSV(data, fileName)
      dispatch(
        addToaster({
          id: uniqueId(),
          message: NAMESPACES.EXPORT_SUCCESSFUL,
          type: 'success',
        })
      )
    } catch (error: unknown) {
      let message = NAMESPACES.EXPORT_ERROR
      let type: ToasterTypeValues = ToasterType.ERROR

      if (isAxiosError(error)) {
        const backendMessage = error.response?.data?.message
        const statusCode = error.response?.status

        if (statusCode === HTTP_STATUS_CONSTANTS.CONFLICT && backendMessage) {
          const parsed = parseErrorMessage(backendMessage)

          if (parsed.type === NAMESPACES.WARN) {
            message = parsed.message
            type = ToasterType.WARNING
          }
        }
      }
      dispatch(
        addToaster({
          id: uniqueId(),
          message,
          type,
        })
      )
      console.log(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleClientChange = () => {
    dispatch(updateCorrespondenceFilterData(null))
    setTableData([])
    setEmptyDataMessage('')
    filterTableData()
    if (!isCaseView) {
      switch (uniqueKey) {
        case correspondenceLogsPage.OUTBOUND_LOWERCASE:
          navigate(
            `/correspondence/correspondence-logs/outbound/${selectedTenantId}`
          )
          break
        case correspondenceLogsPage.INBOUND_LOWERCASE:
          navigate(
            `/correspondence/correspondence-logs/inbound/${selectedTenantId}`
          )
          break
        case correspondenceLogsPage.INBOUND_SMS_LOWERCASE:
          navigate(
            `/correspondence/correspondence-logs/inbound-sms/${selectedTenantId}`
          )
          break
        case correspondenceLogsPage.INBOUND_WEB_KEY:
          navigate(
            `/correspondence/correspondence-logs/inbound-web/${selectedTenantId}`
          )
          break
        case correspondenceLogsPage.INBOUND_MAIL_LOWERCASE:
          navigate(
            `/correspondence/correspondence-logs/inbound-mail/${selectedTenantId}`
          )
          break
        default:
          break
      }
    }
  }
  const getSelectedTenantName = (selectedTenantId: number) => {
    const selectedTenants = allTenantsOptions?.filter(
      (tenant) => tenant.value === selectedTenantId?.toString()
    )
    if (selectedTenants.length > 0) {
      return selectedTenants[0].label
    } else {
      return ''
    }
  }

  const convertToFilters = (
    input: InputItem[],
    keyMap: Record<string, string>,
    sortKeyMap: Record<string, string>
  ): FilterEntry[] => {
    const result: FilterEntry[] = []
    const processedInput: InputItem[] = input.flatMap((item) => {
      if (item?.key.toUpperCase() === 'AGENT_NAME') {
        const [first, last] = (item?.value?.[0] ?? '')
          .split('|')
          .map((v) => v.trim())

        const processed: InputItem[] = []

        if (first) {
          processed.push({
            ...item,

            key: 'users.first_name',

            value: [first],
          })
        } else {
          processed.push({
            ...item,

            key: 'users.first_name',

            value: [''],
          })
        }

        if (last) {
          processed.push({
            ...item,

            key: 'users.last_name',

            value: [last],
          })
        } else {
          processed.push({
            ...item,

            key: 'users.last_name',

            value: [''],
          })
        }

        return processed
      } else if (
        item?.key === correspondenceLogsPage.RECEIVED_AT_KEY.toUpperCase() ||
        item?.key === correspondenceLogsPage.CREATED_AT_KEY.toUpperCase()
      ) {
        return [
          {
            ...item,
            value: item?.value?.map((val) =>
              convertToUTCISOString(formatLocalDateWithZone(val ?? ''))
            ),
          },
        ]
      }

      return item
    })
    processedInput?.forEach((item) => {
      const mappedKey = keyMap[item?.key] ?? sortKeyMap[item?.key] ?? item?.key

      if (mappedKey) {
        const filterEntry: FilterEntry = {
          field_name: mappedKey.toLowerCase(),
          values: [],
          operation: item?.operator ?? 'LIKE',
        }

        const isSortField =
          mappedKey === (sortKeyMap[sortDirection.key] ?? sortDirection.key)

        if (isSortField) {
          filterEntry.direction = sortDirection.direction.toUpperCase()
          if (item?.value?.[0] !== undefined && item?.value?.[0] !== null) {
            filterEntry.values = item?.value ?? []
          } else {
            filterEntry.values = []
          }
        } else {
          if (
            mappedKey === 'users.first_name' ||
            mappedKey === 'users.last_name'
          ) {
            if (item?.value?.[0]) {
              filterEntry.values = item?.value ?? []
            }
          } else if (
            item?.value?.[0] !== undefined &&
            item?.value?.[0] !== null
          ) {
            filterEntry.values = item?.value ?? []
          }
        }

        result.push(filterEntry)
      }
    })

    return result
  }
  const mergeFilters = (filters: FilterEntry[]): FilterEntry[] => {
    const mergedMap = new Map<string, FilterEntry>()

    for (const filter of filters) {
      const key = filter.field_name
      if (mergedMap.has(key)) {
        const existing = mergedMap.get(key)!
        const mergedValues = Array.from(
          new Set([...existing.values, ...filter.values])
        )
        mergedMap.set(key, { ...existing, values: mergedValues })
      } else {
        mergedMap.set(key, filter)
      }
    }

    return Array.from(mergedMap.values())
  }

  const getFormattedSenderName = (
    senderName: string | undefined,
    communicationType: string | undefined
  ): string => {
    if (!senderName) return ''

    const isLetter =
      communicationType?.toLowerCase() === 'letter' ||
      communicationType?.toLowerCase() === 'email' ||
      communicationType?.toLowerCase() === 'postalmail'
    return isLetter
      ? senderName
      : formatPhoneNumber(senderName, FORMAT_PATTERN.PHONE_INPUT) ?? ''
  }

  const preprocessFilters = (
    filters: SearchTableCriteria[]
  ): SearchTableCriteria[] => {
    const processed: SearchTableCriteria[] = []
    filters.forEach((filter) => {
      if (filter.search_key === 'AGENT_NAME') {
        const [firstName, lastName] = filter.search_value[0]
          .toString()
          .split('|')

        if (firstName?.trim()) {
          processed.push({
            search_key: 'FIRST_NAME',
            search_value: [firstName.trim()],
            operator: filter.operator,
          })
        }

        if (lastName?.trim()) {
          processed.push({
            search_key: 'LAST_NAME',
            search_value: [lastName.trim()],
            operator: filter.operator,
          })
        }
      } else if (filter.search_key === 'RECEIVER') {
        processed.push({
          search_key: 'RECIPIENT_RECEIVER',
          search_value: filter.search_value,
          operator: filter.operator,
        })
      } else if (filter.search_key === 'RECIPIENT') {
        processed.push({
          search_key: 'RECIPIENT_ID',
          search_value: filter.search_value,
          operator: filter.operator,
        })
      } else {
        processed.push(filter)
      }
    })

    return processed
  }

  const fetchWebOutboundData = async () => {
    try {
      dispatch(showLoader())
      let modifiedApiFilter = apiFilter
      if (isCaseView && caseId) {
        modifiedApiFilter = {
          ...apiFilter,
          search_criteria: {
            ...apiFilter?.search_criteria,
            CASE_ID: {
              search_key: correspondenceLogsPage.CASE_ID_KEY_UPPERCASE,
              operator: correspondenceLogsPage.EQUALS,
              search_value: [caseId],
            },
          },
        }
      }
      let temporaryObject = Object.entries(
        modifiedApiFilter.search_criteria ?? {}
      ).map(([_key, item]) => ({
        key: item.search_key,
        value: item.search_value,
        operator: item.operator ?? correspondenceLogsPage.LIKE,
      }))
      temporaryObject = temporaryObject?.map((item) => {
        if (item?.key === correspondenceLogsPage.STATUS.toUpperCase()) {
          return {
            ...item,
            value: [item?.value?.toString().toLowerCase().split(' ').join('_')],
          }
        }
        return item
      })
      if (sortDirection) {
        temporaryObject.push({
          key: sortDirection?.key,
          value: [],
          operator: '',
        })
      }

      const filters = convertToFilters(temporaryObject, keyMap, sortKeyMap)
      const mergedFilter = mergeFilters(filters)
      filteredPayload = buildFilterPayload(mergedFilter)
      const response = await correspondenceInboundWebData(
        filteredPayload as Parameters<typeof correspondenceInboundWebData>[0]
      )
      const data = response?.data?.content ?? []
      const numberOfElements = response?.data?.numberOfElements ?? 0
      const totalPages = response?.data?.totalPages ?? 0
      const totalElements = response?.data?.totalElements ?? 0
      setInboundWebPagination({
        numberOfElements: numberOfElements,
        totalPages: totalPages,
        totalElements: totalElements,
      })

      if (data?.length) {
        const transformedData = transformInboundWebData(data)
        setInboundWebTableData(transformedData)
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchCorrespondenceLogsData = async () => {
    if (apiFilter && !isInitialCall?.current) {
      if (uniqueKey !== correspondenceLogsPage.INBOUND_WEB_KEY) {
        const trim = trimObjects(correspondenceLogFormData)
        const searchCriteria = Object.keys(trim).map((key) => ({
          search_key:
            key.toUpperCase() ===
            correspondenceLogsPage.COMMUNICATION_ID_KEY.toUpperCase()
              ? correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE
              : key.toUpperCase(),
          search_value: trim?.[key as keyof CorrespondenceOptions].map((item) =>
            item?.toUpperCase()
          ),
          operator: correspondenceLogsPage.EQUALS,
        }))

        let searchFromTableCriterias: SearchTableCriteria[] = (Object.values(
          apiFilter?.search_criteria ?? {}
        )
          .map((item) =>
            getValidKeys(item?.search_key)
              ? {
                  search_key: getValidKeys(item?.search_key),
                  search_value: item?.search_value,
                  operator: item?.operator ?? correspondenceLogsPage.LIKE,
                }
              : null
          )
          .filter(Boolean) ?? []) as SearchTableCriteria[]
        searchFromTableCriterias = searchFromTableCriterias?.map((item) => {
          if (
            item?.search_key === correspondenceLogsPage.STATUS.toUpperCase()
          ) {
            return {
              ...item,
              search_value: item?.search_value?.map((val: string | number) =>
                val.toString().toLowerCase().split(' ').join('_')
              ),
            }
          }
          return item
        })

        const processed = preprocessFilters(searchFromTableCriterias)
        setSearchFromTableCriterias(processed)
        const payload = {
          fields: fields,
          search_criteria: [
            {
              search_key: correspondenceLogsPage.COMMUNICATION_STATUS_KEY,
              search_value: [
                uniqueKey === correspondenceLogsPage.OUTBOUND_LOWERCASE
                  ? correspondenceLogsPage.OUTBOUND
                  : correspondenceLogsPage.INBOUND,
              ],
              operator: correspondenceLogsPage.EQUALS,
            },
            {
              search_key: correspondenceLogsPage.RECORD_STATUS_KEY,
              search_value: [correspondenceLogsPage.ACTIVE],
              operator: correspondenceLogsPage.EQUALS,
            },
            ...processed,
            ...searchCriteria,
          ],
          page: FIELD_VALUE_CONSTANTS.ONE,
          size: rowPerPage,
          sort: !isEmpty(sortDirection?.keyName)
            ? `${sortDirection?.keyName},${sortDirection?.direction}`
            : undefined,
        }
        if (
          uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE ||
          uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE ||
          uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
        ) {
          payload.search_criteria.push({
            search_key: correspondenceLogsPage.COMMUNICATION_ID_KEY_UPPERCASE,
            search_value: [
              uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE
                ? searchValues.INBOUND_SMS
                : uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
                ? searchValues.INBOUND_MAIL
                : searchValues.INBOUND_FAX,
            ],
            operator: correspondenceLogsPage.CONTAINS,
          })
        }
        if (isCaseView && caseId) {
          payload.search_criteria.push({
            search_key: correspondenceLogsPage.CASES,
            search_value: [caseId],
            operator: correspondenceLogsPage.EQUALS,
          })
        }

        setCurrentPage(FIELD_VALUE_CONSTANTS.ONE)
        dispatch({
          type: filterCorrespondenceLogsData.type,
          payload: payload,
        })
      } else {
        try {
          dispatch(showLoader())
          let modifiedApiFilter = apiFilter
          if (isCaseView && caseId) {
            modifiedApiFilter = {
              ...apiFilter,
              search_criteria: {
                ...apiFilter?.search_criteria,
                CASE_ID: {
                  search_key: correspondenceLogsPage.CASE_ID_KEY_UPPERCASE,
                  operator: correspondenceLogsPage.EQUALS,
                  search_value: [caseId],
                },
              },
            }
          }
          let temporaryObject = Object.entries(
            modifiedApiFilter.search_criteria ?? {}
          ).map(([_key, item]) => ({
            key: item.search_key,
            value: item.search_value,
            operator: item.operator,
          }))
          temporaryObject = temporaryObject?.map((item) => {
            if (item?.key === correspondenceLogsPage.STATUS.toUpperCase()) {
              return {
                ...item,
                value: [
                  item?.value?.toString().toLowerCase().split(' ').join('_'),
                ],
              }
            }
            return item
          })

          if (sortDirection) {
            temporaryObject.push({
              key: sortDirection?.key,
              value: [],
              operator: '',
            })
          }
          const filters = convertToFilters(temporaryObject, keyMap, sortKeyMap)
          const mergedFilter = mergeFilters(filters)
          filteredPayload = buildFilterPayload(mergedFilter)

          const response = await correspondenceInboundWebData(
            filteredPayload as Parameters<
              typeof correspondenceInboundWebData
            >[0]
          )
          const data = response?.data?.content ?? []
          const numberOfElements = response?.data?.numberOfElements ?? 0
          const totalPages = response?.data?.totalPages ?? 0
          const totalElements = response?.data?.totalElements ?? 0
          setInboundWebPagination({
            numberOfElements: numberOfElements,
            totalPages: totalPages,
            totalElements: totalElements,
          })
          if (data?.length) {
            const transformedData = transformInboundWebData(data)
            setInboundWebTableData(data?.length ? transformedData : [])
          } else {
            setInboundWebTableData([])
          }
        } catch (error) {
          console.error(error)
          setInboundWebTableData([])
        } finally {
          dispatch(hideLoader())
        }
      }
    }
  }

  useEffect(() => {
    fetchCorrespondenceLogsData()
  }, [apiFilter, correspondenceLogFormData, refetchKey, refetchTableKey])

  //outbound table data
  useEffect(() => {
    correspondenceFilteredLogsData?.content?.length
      ? setEmptyDataMessage('')
      : setEmptyDataMessage(correspondenceLogsPage.NO_CORRESPONDENCE_DATA)
    if (correspondenceFilteredLogsData) {
      const transformedData = correspondenceFilteredLogsData?.content?.map(
        (logs) => ({
          case_id: logs?.case_id?.toString(),
          body:
            logs.category_communication_type_id === COMM_TEMPLATE.SMS_UPPERCASE
              ? logs?.body
              : '',
          template_name: logs?.template_name,
          program: logs?.programs_id?.operationalProgramName,
          created_at:
            formatLocalDateWithZone(logs?.created_at as string) ?? '--',
          received_at:
            formatLocalDateWithZone(logs?.created_at as string) ?? '--',
          agent_name: `${logs?.users_id?.firstName ?? ''} ${
            logs?.users_id?.firstName && logs?.users_id?.lastName ? '|' : ''
          } ${logs?.users_id?.lastName ?? ''}`.trim(),
          modified_by: logs?.modified_by,
          communication_type: logs?.category_communication_type_id,
          status: logs?.category_status_id,
          communication_id: logs?.communication_id,
          reviewer: logs?.reviewer,
          sender_name: getFormattedSenderName(
            logs?.sender_name as string | undefined,
            logs?.category_communication_type_id
          ),
          documentEntities: logs?.document_entities,
          program_id: logs?.programs_id?.id,
          id: logs?.id,
          case_key: logs?.cases
            ?.map((item) => item?.case_key)
            .filter(Boolean)
            ?.join(', '),
          service: logs?.cases
            ?.map((item) => item?.services)
            .filter(Boolean)
            ?.join(', '),
          recipient: [
            ...new Set(
              logs?.communication_recipient?.map((item) => item?.record_key)
            ),
          ]
            .filter(Boolean)
            ?.join(', '),
          recipient_type: [
            ...new Set(
              logs?.communication_recipient
                ?.map((item) => item?.category?.toLowerCase())
                .filter(Boolean)
                .map(
                  (category) =>
                    category.charAt(0).toUpperCase() + category.slice(1)
                )
            ),
          ]?.join(', '),
          receiver:
            logs?.view_object
              ?.map((item) => {
                const recipients =
                  item?.recipient &&
                  item?.recipient !== correspondenceLogsPage.NOT_AVAILABLE
                    ? item?.recipient
                    : ''
                return ['EMAIL', 'LETTER']?.includes(
                  logs?.category_communication_type_id ?? ''
                )
                  ? recipients
                  : formatPhoneNumber(recipients, FORMAT_PATTERN.PHONE_INPUT)
              })
              .filter(Boolean)
              .join(', ') ||
            formatPhoneNumber(
              logs?.fax_recipientTo?.[0] || '',
              FORMAT_PATTERN.PHONE_INPUT
            ),
          product: logs?.product?.trademarkedName,
          message_id: logs?.message_id,
          status_reason: logs?.reason,
        })
      )
      setTableData(transformedData as TransformedDataType[])
    }
  }, [correspondenceFilteredLogsData])

  useEffect(() => {
    if (uniqueKey === correspondenceLogsPage.OUTBOUND_LOWERCASE) {
      setDefaultActiveTab({
        label: correspondenceLogsPage.OUTBOUND,
        component: null,
      })
    }
    if (uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE) {
      setDefaultActiveTab({
        label: correspondenceLogsPage.INBOUND_FAX,
        component: null,
      })
    }
    if (uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE) {
      setDefaultActiveTab({
        label: correspondenceLogsPage.INBOUND_SMS,
        component: null,
      })
    }
    if (uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY) {
      setDefaultActiveTab({
        label: correspondenceLogsPage.INBOUND_WEB,
        component: null,
      })
    }
    if (uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE) {
      setDefaultActiveTab({
        label: correspondenceLogsPage.INBOUND_MAIL,
        component: null,
      })
    }
  }, [uniqueKey])

  useEffect(() => {
    if (uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY) {
      fetchWebOutboundData()
    }
  }, [uniqueKey])

  useEffect(() => {
    if (previewData?.type)
      setOpenPreviewModal({
        value: booleanValues.TRUE,
        type: previewData?.type,
      })
  }, [previewData])
  useEffect(() => {
    if (isSuperAdmin || !isEmpty(programPermissions)) {
      filterTableData()
      const areAllArraysEmpty = Object.values(allOptions).every(
        (arr) => Array.isArray(arr) && arr.length === 0
      )
      if (areAllArraysEmpty) {
        getDropdownOptions()
      }
      isInitialCall.current = false
    }
  }, [sortDirection, programPermissions])

  useEffect(() => {
    if (inboundBulk) {
      if (staticURL || dynamicURL) {
        downloadEmailContent()
        setInboundBulk(false)
      }
    }
  }, [inboundBulk, staticURL, dynamicURL])

  useEffect(() => {
    if (!isSuperAdmin) {
      const view = programPermissionsObject.Correspondence_Logs_View(
        programPermissions,
        PERMISSION_MODULES.CORRESPONDENCE_LOGS,
        'View Only'
      )
      const viewPermissions = view.map((program_id) => programs[program_id])
      const managePermissions = permissionObject.Correspondence_Logs_Manage(
        programPermissions,
        PERMISSION_MODULES.CORRESPONDENCE_LOGS,
        'Manage'
      )
      permissionsData.current = {
        viewPermissions: viewPermissions,
        hasManagePermissions: managePermissions,
      }
      if (viewPermissions?.length) {
        filterTableData(viewPermissions)
      }
    }
  }, [programPermissions, programPermissionsObject])

  useEffect(() => {
    dispatch(
      setClientDropdown({
        isVisible: true,
        onChange: handleClientChange,
        isGlobalVisible: false,
      })
    )
  }, [location])

  return (
    <div className="font-primary h-full bg-accent-0 flex flex-col">
      <form
        onSubmit={() => {
          return
        }}
      >
        {!isCaseView && (
          <div className="flex items-center font-medium text-base p-3 border-b-2">
            <span>
              {CORRESPONDENCE_LOGS_STRINGS.CORRESPONDENCE_LOGS}&nbsp;-&nbsp;
              {getSelectedTenantName(selectedTenantId ?? -1) ||
                CORRESPONDENCE_LOGS_STRINGS.GLOBAL}
            </span>
          </div>
        )}
        <div className="flex mt-1">
          {!isCaseView && (
            <Tabs
              tabs={tabsList}
              onChange={handleTabChange}
              defaultActive={defaultActiveTab}
              tabsWrapperClassName="flex-1"
            />
          )}
          <div className="flex items-center justify-center ml-auto gap-2"></div>
          {!isReadOnlyUser() ? (
            <div className="flex items-center justify-start">
              {!isCaseView &&
                (isSuperAdmin ||
                  permissionsData.current?.hasManagePermissions) && (
                  <div className="mr-2">
                    <ExportButtonDropdown
                      showCSV
                      showExcel
                      onExportOption={onExportOption}
                      customCSS="-translate-x-3/4"
                      dataQaId="correspondence-export-btn"
                    />
                  </div>
                )}
            </div>
          ) : null}
        </div>
      </form>
      {(isSuperAdmin || permissionsData.current?.viewPermissions?.length) &&
      showTableType === correspondenceLogsPage.OUTBOUND_LOWERCASE ? (
        <div className="flex-1 min-h-0">
          <div className="h-full">
            <div className="w-full h-full flex flex-col">
              <PersistTable
                tableName={`correspondenceLogsOutboundTable-${selectedTenantId}`}
                data={(tableData || []) as DataTypeStore[]}
                columns={outboundColumns || []}
                actions={tableActions}
                onActionClick={onActionClick}
                isColumnChooser
                onSortClick={onSortClick}
                sort={getSortValue(sortDirection)}
                disabledKey={
                  correspondenceLogsPage.DISABLED as keyof TransformedDataType
                }
                key={refetchTableKey}
                showTableColumnOnEmpty
                isPersistColumns={true}
                filterObject={apiFilter}
                onFilterChange={onFilterChange}
                emptyDataMessage={emptyDataMessage}
                dataQaId={'correspondence-logs-outbound-table'}
                showRestoreButton={true}
              />
              {tableData?.length ? (
                <Pagination
                  numberOfElements={
                    correspondenceFilteredLogsData?.numberOfElements
                  }
                  rowsPer={FIELD_VALUE_CONSTANTS.TEN}
                  totalElements={correspondenceFilteredLogsData?.totalElements}
                  currentPage={currentPage}
                  totalPages={totalPages ?? 0}
                  onPageChange={handleUserPageChange}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {(isSuperAdmin || permissionsData.current.viewPermissions?.length) &&
      (showTableType === correspondenceLogsPage.INBOUND_LOWERCASE ||
        showTableType === correspondenceLogsPage.INBOUND_SMS_LOWERCASE ||
        showTableType === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE) ? (
        <div className="flex-1 min-h-0">
          <div className="h-full">
            <div className="w-full h-full flex flex-col">
              <PersistTable
                showTableColumnOnEmpty
                tableName={`${
                  uniqueKey === correspondenceLogsPage.INBOUND_SMS_LOWERCASE
                    ? `correspondenceLogsInboundSMSTable-${selectedTenantId}`
                    : uniqueKey ===
                      correspondenceLogsPage.INBOUND_MAIL_LOWERCASE
                    ? `correspondenceLogsInboundMailTable-${selectedTenantId}`
                    : `correspondenceLogsInboundTable-${selectedTenantId}`
                }`}
                data={(tableData || []) as DataTypeStore[]}
                columns={inboundColumns || []}
                onSortClick={onSortClick}
                isColumnChooser
                onFilterChange={onFilterChange}
                filterObject={apiFilter}
                sort={getSortValue(sortDirection, uniqueKey)}
                disabledKey={
                  correspondenceLogsPage.DISABLED as keyof TransformedDataType
                }
                isPersistColumns={true}
                emptyDataMessage={emptyDataMessage}
                dataQaId={'correspondence-logs-inbound-table'}
                showRestoreButton={true}
              />
              {tableData?.length ? (
                <Pagination
                  numberOfElements={
                    correspondenceFilteredLogsData?.numberOfElements
                  }
                  rowsPer={FIELD_VALUE_CONSTANTS.TEN}
                  totalElements={correspondenceFilteredLogsData?.totalElements}
                  currentPage={currentPage}
                  totalPages={totalPages ?? 0}
                  onPageChange={handleUserPageChange}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {(isSuperAdmin || permissionsData.current.viewPermissions?.length) &&
      showTableType === correspondenceLogsPage.INBOUND_WEB_KEY ? (
        <div className="flex-1 min-h-0">
          <div className="h-full">
            <div className="w-full h-full flex flex-col">
              <PersistTable
                showTableColumnOnEmpty
                tableName={`correspondenceLogsInboundWebTable-${selectedTenantId}`}
                data={(inboundWebTableData || []) as DataTypeStore[]}
                columns={inboundColumns || []}
                onSortClick={onSortClick}
                isColumnChooser
                onFilterChange={onFilterChange}
                filterObject={apiFilter}
                sort={getSortValue(sortDirection)}
                disabledKey={
                  correspondenceLogsPage.DISABLED as keyof TransformedDataType
                }
                isPersistColumns={true}
                emptyDataMessage={emptyDataMessage}
                dataQaId={'correspondence-logs-inbound-web-table'}
                showRestoreButton={true}
              />
              {inboundWebTableData?.length ? (
                <Pagination
                  numberOfElements={inboundWebPagination?.numberOfElements}
                  rowsPer={FIELD_VALUE_CONSTANTS.TEN}
                  totalElements={inboundWebPagination?.totalElements}
                  currentPage={currentPage}
                  totalPages={inboundWebPagination?.totalPages ?? 0}
                  onPageChange={handleUserPageChange}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {openPreviewModal.value && (
        <PreviewCorrespondenceLogs
          downloadEmailContent={downloadEmailContent}
          staticURL={staticURL}
          dynamicURL={dynamicURL}
          setDynamicURL={setDynamicURL}
          setStaticURL={setStaticURL}
          uniqueKey={uniqueKey}
          openPreviewModal={openPreviewModal}
          previewTemplateData={previewData}
          handlePreviewClose={handlePreviewClose}
          setOpenPreviewModal={setOpenPreviewModal}
          setRefetchKey={setRefetchKey}
          tableType={showTableType}
          key={previewData?.communicationId}
        />
      )}
    </div>
  )
}
export default CorrespondenceLogsTable

const CommunicationModeHandler = ({
  baseClass,
  svg,
  communicationModeText,
}: {
  baseClass: string
  svg?: ReactNode
  communicationModeText: string
}) => {
  return (
    <div className={`${baseClass}`}>
      {svg}
      <span className="font-light text-xs">{communicationModeText}</span>
    </div>
  )
}

const CommunicationMode = ({ mode }: { mode: string }) => {
  const baseClass =
    'flex items-center p-1 w-fit truncate block rounded text-center gap-2 group'
  switch (mode) {
    case emailTemplate.EMAIL:
      return (
        <CommunicationModeHandler
          baseClass={baseClass}
          // svg={<SvgEmailOutlined fill="primary-900" className="mr-2" />}
          communicationModeText={emailTemplate.Email}
        />
      )
    case emailTemplate.LETTER:
      return (
        <CommunicationModeHandler
          baseClass={baseClass}
          // svg={<SvgLetterOutlined fill="primary-900" className="mr-2" />}
          communicationModeText={emailTemplate.Letter}
        />
      )
    case emailTemplate.SMS:
      return (
        <CommunicationModeHandler
          baseClass={baseClass}
          // svg={<SvgSmsOutlined fill="primary-900" className="mr-2" />}
          communicationModeText={emailTemplate.Sms}
        />
      )
    case emailTemplate.FAX:
      return (
        <CommunicationModeHandler
          baseClass={baseClass}
          // svg={<SvgFaxOutlined fill="primary-900" className="mr-2" />}
          communicationModeText={emailTemplate.Fax}
        />
      )
    case emailTemplate.POSTALMAIL:
      return (
        <CommunicationModeHandler
          baseClass={baseClass}
          communicationModeText={emailTemplate.Mail}
        />
      )
    default:
      return null
  }
}

const QuickDenialMsgViewer = ({
  quickDenialReason = '',
  quickDenialReasonObj = '',
}: {
  quickDenialReason: string
  quickDenialReasonObj: string
}) => {
  if (!quickDenialReasonObj)
    return (
      <span className={`font-light text-xs w-full truncate block`}>--</span>
    )

  const messages = quickDenialReasonObj.split('$')

  return (
    <Tooltip
      content={
        <div style={{ whiteSpace: 'pre-line' }}>{messages.join('\n')}</div>
      }
      position="top"
      className={
        (quickDenialReason as string)?.length < 20 ? 'w-fit' : 'max-w-96'
      }
    >
      <span className={`font-light text-xs w-full truncate block`}>
        {quickDenialReason !== undefined && quickDenialReason !== null
          ? (quickDenialReason as React.ReactNode)
          : '--'}
      </span>
    </Tooltip>
  )
}

const CaseKeyRenderer = ({
  uniqueKey,
  row,
  rowIndex,
  handleCaseKeyClick,
}: {
  uniqueKey: string
  row: TransformedDataType
  rowIndex: number
  handleCaseKeyClick: (
    uniqueKey: string,
    _row: TransformedDataType,
    _rowIndex: number
  ) => Promise<void>
}) => {
  const case_no =
    uniqueKey === correspondenceLogsPage.INBOUND_WEB_KEY
      ? row?.case_id
      : row?.case_key
  return (
    <button
      className={`text-secondary-500 font-medium ${
        case_no ? 'cursor-pointer' : 'cursor-text'
      }`}
      onClick={() => handleCaseKeyClick(uniqueKey, row, rowIndex)}
    >
      <Tooltip content={case_no} position="top" className="w-fit">
        {case_no ?? '--'}
      </Tooltip>
    </button>
  )
}

const CommunicationIdRenderer = ({
  row,
  rowIndex,
  onActionClick,
  openInNewTab,
}: {
  row: TransformedDataType
  rowIndex: number
  onActionClick: (
    actionKeyName: string,
    _row: TransformedDataType,
    _rowIndex: number
  ) => void
  openInNewTab?: () => void
}) => {
  const handleClick = () => {
    if (row?.communication_id) {
      if (openInNewTab) {
        openInNewTab()
      } else {
        onActionClick('view', row, rowIndex)
      }
    }
  }

  return (
    <button
      className={`text-secondary-500 font-medium ${
        row?.communication_id ? 'cursor-pointer' : 'cursor-text'
      }`}
      onClick={handleClick}
    >
      <Tooltip content={row?.communication_id} position="top" className="w-fit">
        {row?.communication_id ?? '--'}
      </Tooltip>
    </button>
  )
}
